/*
 * ELTANE — Personalized Fuel Planner
 *
 * One-question-per-screen wizard. Collects the answers the rule-based
 * recommendation API expects (Q1–Q8), POSTs them to /api/recommend, and renders
 * the returned carb / sodium / cramp plan plus the cart-ready product list.
 *
 * Markup contract (see sections/eltane-fuel-planner.liquid):
 *   [data-planner]                    root; carries data-api-base, data-cart-add-url, data-cart-url
 *     [data-planner-open]             reveals the wizard
 *     [data-planner-panel="NAME"]     intro | wizard | loading | results | error
 *     [data-planner-step]             one per question; index 0 shown first
 *     [data-planner-field="KEY"]      input whose value maps to an API key
 *     [data-planner-progress-bar]     inline width set to completion %
 *     [data-planner-progress-text]    "Question N of M"
 *     [data-planner-back|next|submit] navigation
 *     [data-planner-error]            per-step validation message
 *     [data-planner-results]          result card renders in here
 *     [data-planner-map]              one per mapping; data-api-product-id + data-variant-id
 *
 * The API supplies defaults for every field, so a merchant removing a question
 * block degrades the plan rather than breaking it.
 */
(function () {
  'use strict';

  var LB_PER_KG = 2.20462;

  /* Everything the validator and the nav gate look at. Payload membership is a
     separate question, decided by data-planner-field alone — see bind(). */
  var VALIDATED_INPUTS = '[data-planner-field], [data-planner-validate]';

  /* Bounds and prompts per weight unit. The kg figures mirror what the input
     shipped with; the lb ones are those converted and rounded INWARDS, so that
     every accepted pound value still lands inside the kg range once converted.
     Rounding outwards instead would let 66 lb through as 29.9 kg, under the
     30 kg floor the rest of the planner assumes. */
  var WEIGHT_UNITS = {
    kg: { label: 'Kilograms', min: 30, max: 200, step: 0.5, placeholder: '70' },
    lb: { label: 'Pounds', min: 67, max: 440, step: 1, placeholder: '155' }
  };

  /* Swim / bike / run km per standard triathlon — must match the API's
     planner_config.TRIATHLON_PRESETS and the preset chips in the section. */
  var TRIATHLON_FORMATS = [
    { format: 'Sprint', km: [0.75, 20, 5] },
    { format: 'Olympic', km: [1.5, 40, 10] },
    { format: '70.3', km: [1.9, 90, 21.1] },
    { format: 'Ironman', km: [3.8, 180, 42.2] }
  ];

  function triathlonFormat(swim, bike, run) {
    var given = [parseFloat(swim), parseFloat(bike), parseFloat(run)];
    for (var i = 0; i < TRIATHLON_FORMATS.length; i++) {
      var km = TRIATHLON_FORMATS[i].km;
      if (given[0] === km[0] && given[1] === km[1] && given[2] === km[2]) return TRIATHLON_FORMATS[i].format;
    }
    return 'Custom';
  }

  /*
   * The form's answers -> the /api/recommend body. The questions keep their
   * long-standing field names (intensity, cramp_history) so saved progress
   * and the summary chips keep working; the API's current names are mapped
   * here, at the one place the request is built.
   */
  function apiPayload(answers) {
    var payload = {};
    for (var key in answers) {
      if (Object.prototype.hasOwnProperty.call(answers, key)) payload[key] = answers[key];
    }
    if (payload.intensity) {
      payload.effort = payload.intensity;
      delete payload.intensity;
    }
    if (payload.cramp_history) {
      payload.cramp_prone = payload.cramp_history;
      delete payload.cramp_history;
    }
    return payload;
  }

  var CAFFEINE_USE_LABEL = { regular: 'Yes', rare: 'Yes', none: 'No' };

  /* Climate keys the API returns in weather.climate. */
  var CLIMATE_LABEL = { cold: 'Cold', mild: 'Mild', hot: 'Hot', very_hot: 'Very hot' };

  /* "Hot", "Very hot · humid", or "Indoor" for Hyrox. */
  function climateLabel(weather) {
    if (!weather) return '';
    if (weather.indoor) return 'Indoor';
    var label = CLIMATE_LABEL[weather.climate] || '';
    return label && weather.humidity_high ? label + ' · humid' : label;
  }

  /* Keys must match the flavour keys the API returns in carb_plan.flavor_breakdown
     and events[].product_flavour — both the older names and the v2.1 short ones
     map to the retail flavour names. */
  var FLAVOR_LABEL = {
    Unflavoured: 'Original',
    Neutral: 'Original',
    'Alphonso Mango': 'Mango Rush',
    Mango: 'Mango Rush',
    'Lime Mint': 'Lemon Lime',
    Lemon: 'Lemon Lime',
    Original: 'Original',
    Coffee: 'Classic Coffee (Caffeinated)',
    'Coffee (Caffeinated)': 'Classic Coffee (Caffeinated)'
  };

  /* Always shown under the results, whatever disclaimer the API sends. */
  var PLAN_DISCLAIMER =
    'This plan is general guidance. Practise your fuelling, fluids and caffeine in training before race day. ' +
    'If you have a medical condition, speak to your doctor first.';

  /* API advice the results deliberately leave out: the gel-cap notice, the
     solid-fuel tip, and the caffeine reminder the coffee-gel note already gives. */
  var HIDDEN_NOTE_PATTERNS = [
    /^\s*a plan is capped at \d+ gels/i,
    /^\s*practi[sc]e taking solid fuel/i,
    /* Anchored both ends: the coffee-gel note mentions tested caffeine too. */
    /^\s*only use caffeine you have tested in training\.?\s*$/i
  ];

  function isHiddenNote(text) {
    return HIDDEN_NOTE_PATTERNS.some(function (pattern) { return pattern.test(text); });
  }

  /* Older API copy still says pickle juice "may help with cramp". */
  function crampCopy(text) {
    return String(text).replace(
      /pickle juice may help with cramps?/i,
      'Pickle juice helps mitigate cramps and provide rapid cramp relief'
    );
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = String(text);
    return node;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Marks a node for the staggered rise-in defined in the stylesheet. The index
     becomes the animation delay, so a row of tiles/chips/phases arrives one
     after another rather than all at once. */
  function reveal(node, index) {
    node.classList.add('eltane-planner__reveal');
    node.style.setProperty('--i', String(index || 0));
    return node;
  }

  /* Build all result markup with textContent so API strings are never parsed as HTML.
     `detail`, when given, is a small explanatory line under the value — e.g. the
     "850 mg needed − 225 mg from gels" breakdown under Extra sodium needed. */
  function tile(label, value, unit, detail, index) {
    var wrap = el('div', 'eltane-planner__tile');
    wrap.appendChild(el('span', 'eltane-planner__tile-label', label));

    var val = el('span', 'eltane-planner__tile-value');
    /* The number lives in its own span so it can be styled independently of
       the unit suffix beside it. */
    var figure = el('span', 'eltane-planner__tile-num', value);
    val.appendChild(figure);
    if (unit) val.appendChild(el('span', 'eltane-planner__tile-unit', unit));
    wrap.appendChild(val);
    if (detail) wrap.appendChild(el('span', 'eltane-planner__tile-detail', detail));
    return reveal(wrap, index || 0);
  }

  function num(value, fallback) {
    return value === undefined || value === null ? (fallback === undefined ? '—' : fallback) : value;
  }

  function Planner(root) {
    this.root = root;
    this.apiBase = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
    this.cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add.js';
    this.cartUrl = root.getAttribute('data-cart-url') || '/cart';
    this.brand = root.getAttribute('data-brand') || '';
    this.site = root.getAttribute('data-site') || '';
    this.allowDownload = root.getAttribute('data-allow-download') !== 'false';
    this.contactUrl = root.getAttribute('data-contact-url') || '/contact';
    this.leadEndpoint = (root.getAttribute('data-lead-endpoint') || '').replace(/\/+$/, '');
    /* Sent as X-Api-Key on every call to our own recommendation API (never to
       Shopify's own endpoints — /contact, /cart/add.js). See the API's own
       README: this only screens out casual/opportunistic hits from other
       sites, since a value shipped in storefront JS can always be read from
       devtools — it is not real authentication. */
    this.apiSecretKey = root.getAttribute('data-api-secret-key') || '';
    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-planner-step]'));
    this.index = 0;
    this.plan = null;
    /* True only while the visitor is back in the questions from a results
       chip — see show() and editField(). */
    this.editing = false;
    this.narrationTimer = null;
    this.productMap = this.readProductMap();

    /* The section wrapper carries the anchor id ("fuel-planner"); appending
       "-start" gives a second, wizard-opening address for the same section. See
       openFromHash.

       It is also what gets scrolled, not [data-planner] itself: the header is
       sticky, and the scroll-margin-top that clears it is declared on the
       #fuel-planner id in eltane-base.css. Scrolling the inner element skips
       that margin and parks question 1 under the header. */
    this.anchor = root.closest('[id]');
    this.startHash = this.anchor && this.anchor.id ? '#' + this.anchor.id + '-start' : null;

    /* Set by restoreState() when a refresh lands back on a later step rather
       than a fresh question 1 — read by openFromHash() so it does not reset
       that progress back to the start. */
    this.restoredToStep = false;

    this.bind();
    this.restoreState();
    this.openFromHash();
  }

  /* Headers for a call to our own recommendation API: whatever the caller
     needs, plus X-Api-Key when the merchant has one configured. Never used
     for Shopify's own endpoints (/contact, /cart/add.js) - those don't know
     about this key and don't need it. */
  Planner.prototype.apiHeaders = function (extra) {
    var headers = {};
    for (var key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) headers[key] = extra[key];
    }
    if (this.apiSecretKey) headers['X-Api-Key'] = this.apiSecretKey;
    return headers;
  };

  /* { "P1a": 44123456789, ... } assembled from the [data-planner-map] elements
     the section renders from its Product mapping settings. */
  Planner.prototype.readProductMap = function () {
    var map = {};
    this.root.querySelectorAll('[data-planner-map]').forEach(function (node) {
      var apiId = node.getAttribute('data-api-product-id');
      var variantId = parseInt(node.getAttribute('data-variant-id'), 10);
      var variantId12 = parseInt(node.getAttribute('data-variant-id-12'), 10);
      if (apiId && variantId > 0) {
        var price = node.getAttribute('data-price') || '';
        map[apiId] = {
          /* The real Shopify product id — distinct from apiId ("P1a"…"P3"),
             which is only the recommendation API's own key and means nothing
             to Shopify. Needed for "Notify me" (see buildNotifyBlock): a
             signup sent apiId instead couldn't be matched against Shopify's
             back-in-stock webhook, so the confirmation email never arrived
             even though the signup itself appeared to succeed. */
          productId: node.getAttribute('data-product-id') || '',
          variantId: variantId,
          variantId12: variantId12 > 0 ? variantId12 : variantId,
          title: node.getAttribute('data-title') || '',
          url: node.getAttribute('data-url') || '',
          image: node.getAttribute('data-image') || '',
          price: price,
          price12: node.getAttribute('data-price-12') || price,
          /* Hidden single-sachet product (see the section's "single product"
             settings); 0 when none is mapped or it is sold out. */
          singleVariantId:
            node.getAttribute('data-single-available') === 'true'
              ? parseInt(node.getAttribute('data-single-variant-id'), 10) || 0
              : 0
        };
      }
    });
    return map;
  };

  /* Gels and the pickle shot are sold as packs of 6 and 12, plus a hidden
     single sachet that only the planner sells. The plan's per-item quantity
     (e.g. "8 gels") is filled with the biggest packs first and singles for
     the rest: 8 -> 1 Pack of 6 + 2 singles, 13 -> 1 Pack of 12 + 1 single,
     3 -> 3 singles. Without a single mapped (or with it sold out) it falls
     back to the smallest pack that covers the quantity: up to 6 -> one Pack
     of 6, more -> one Pack of 12. Returns one cart line per variant. */
  Planner.prototype.resolvePackLines = function (item) {
    var mapped = this.productMap[item.product_id];
    if (!mapped) return [];
    var needed = item.quantity > 0 ? item.quantity : 1;
    var hasPack12 = mapped.variantId12 !== mapped.variantId;

    if (!mapped.singleVariantId) {
      var usePack12 = hasPack12 && needed > 6;
      return [{ variantId: usePack12 ? mapped.variantId12 : mapped.variantId, quantity: 1, single: false }];
    }

    var lines = [];
    if (hasPack12 && needed >= 12) {
      lines.push({ variantId: mapped.variantId12, quantity: Math.floor(needed / 12), single: false });
      needed = needed % 12;
    }
    if (needed >= 6) {
      lines.push({ variantId: mapped.variantId, quantity: Math.floor(needed / 6), single: false });
      needed = needed % 6;
    }
    if (needed > 0) lines.push({ variantId: mapped.singleVariantId, quantity: needed, single: true });
    return lines;
  };

  /* Which [data-planner-panel] is currently the visible one — "intro",
     "wizard", "loading", "results" or "error". Used by saveState() to only
     ever persist while the wizard itself is what's on screen. */
  Planner.prototype.currentPanelName = function () {
    var visible = this.root.querySelector('[data-planner-panel]:not([hidden])');
    return visible ? visible.getAttribute('data-planner-panel') : '';
  };

  /* ==================================================================== state
     persistence — surviving a refresh, mid-wizard or on the finished plan.

     sessionStorage, not localStorage: the ask is "don't lose this on a
     refresh", not "remember it forever" — a plan half-answered (or finished
     and forgotten) a week ago should not silently reappear on a later,
     unrelated visit. It also means every tab gets its own in-progress plan
     rather than one shared across however many the visitor has open.

     Two different snapshots share the one storage key, distinguished by
     `panel`:
       'wizard'  — index + every answered field (see saveState), replaced on
                   every step and every edit.
       'results' — the finished plan itself (see saveResultsState), written
                   once a plan is delivered and left alone after that; a
                   refresh on the results screen re-renders from it directly
                   rather than re-asking the API or reopening the wizard.
     Only "Start over" (see bind()'s data-planner-restart handler) clears it
     outright — that is the one place a visitor actually means to discard
     what's saved, wizard progress or a finished plan alike.
     ==================================================================== */

  Planner.prototype.storageKey = function () {
    return 'eltane-fuel-planner:' + (this.anchor && this.anchor.id ? this.anchor.id : this.root.id || 'default');
  };

  /*
   * Snapshots the step index and every answered field into sessionStorage.
   *
   * Radios are recorded by name + value rather than id: an id is only
   * unique per input, but a radio group's own mutual exclusivity (checking
   * one natively unchecks its siblings) only ever applies within inputs
   * that share a name, which is also what restoreState() relies on to
   * avoid leaving two radios in the same group checked.
   */
  /*
   * Every answered field, radios keyed by name + value rather than id: an id
   * is only unique per input, but a radio group's own mutual exclusivity
   * (checking one natively unchecks its siblings) only ever applies within
   * inputs that share a name, which is also what applyFieldSnapshot() relies
   * on to avoid leaving two radios in the same group checked.
   *
   * Collected the same way whichever panel is showing — saveResultsState()
   * needs this alongside the plan itself, since renderResults() still reads
   * a couple of things (the checked sport radio, for the plan's heading)
   * straight from the DOM rather than from the plan or lastPayload.
   */
  Planner.prototype.collectFieldSnapshot = function () {
    var fields = {};
    var radios = {};

    this.root.querySelectorAll('input, textarea, select').forEach(function (el) {
      if (el.type === 'radio') {
        if (el.checked && el.name) radios[el.name] = el.value;
        return;
      }
      /* Every non-radio control this section renders carries a stable id
         (see the liquid snippets) — one that skips it isn't identifiable
         well enough to restore reliably, so it's left out rather than
         guessed at. */
      if (!el.id) return;
      fields[el.id] = el.type === 'checkbox' ? el.checked : el.value;
    });

    return { fields: fields, radios: radios };
  };

  Planner.prototype.saveState = function () {
    if (this.currentPanelName() !== 'wizard') return;

    var snapshot = this.collectFieldSnapshot();
    this.writeSavedState({
      panel: 'wizard',
      index: this.index,
      fields: snapshot.fields,
      radios: snapshot.radios
    });
  };

  /*
   * Called once a plan is actually on screen (see submit()). The plan
   * itself, not just the answers that produced it — restoreState() renders
   * straight from this rather than replaying the wizard and resubmitting,
   * which would also mean emailing the plan and adding it to the marketing
   * list a second time on every refresh. The field snapshot still comes
   * along too — see collectFieldSnapshot()'s comment.
   */
  Planner.prototype.saveResultsState = function () {
    var snapshot = this.collectFieldSnapshot();
    this.writeSavedState({
      panel: 'results',
      fields: snapshot.fields,
      radios: snapshot.radios,
      plan: this.plan,
      lastPayload: this.lastPayload
    });
  };

  Planner.prototype.writeSavedState = function (data) {
    try {
      window.sessionStorage.setItem(this.storageKey(), JSON.stringify(data));
    } catch (e) {
      /* Private browsing, a full quota, or storage disabled outright —
         resuming across a refresh is a convenience, not something worth
         surfacing as an error over. */
    }
  };

  Planner.prototype.clearSavedState = function () {
    try {
      window.sessionStorage.removeItem(this.storageKey());
    } catch (e) {}
  };

  /*
   * "Start over" (see bind()'s and renderCart()'s data-planner-restart
   * handlers) — restores every field to its server-rendered default rather
   * than just navigating back to question 1 while last time's answers sit
   * untouched in the (now hidden) inputs waiting to reappear.
   *
   * Read from each element's own defaultValue/defaultChecked — the
   * original HTML attributes from when the page loaded, unaffected by
   * anything typed or clicked since — rather than a hand-maintained list of
   * "what counts as blank" per field. That also means a field the liquid
   * pre-fills on purpose (the weight slider's 70kg starting point, a
   * triathlon leg's 1:00 placeholder time) comes back to that sensible
   * default instead of an empty box.
   */
  Planner.prototype.resetFields = function () {
    var self = this;

    this.root.querySelectorAll('input, textarea, select').forEach(function (el) {
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = el.defaultChecked;
      } else if (el.tagName === 'SELECT') {
        Array.prototype.forEach.call(el.options, function (opt) {
          opt.selected = opt.defaultSelected;
        });
      } else {
        el.value = el.defaultValue;
      }
    });

    this.plan = null;
    this.lastPayload = null;

    /* None of the above fires 'input'/'change', so every widget that
       shadows a raw value — the weight slider's bounds,
       the distance chips, the intensity graph — needs the same explicit
       re-sync restoreState() already does for the identical reason. */

    this.root.querySelectorAll('[data-planner-weight-unit]:checked').forEach(function (radio) {
      self.applyWeightUnit(radio.value, { scope: radio.closest('[data-planner-step]') || self.root });
    });

    this.root.querySelectorAll('[data-planner-field="distance_km"]').forEach(function (input) {
      self.syncPresets(input);
    });

    this.root.querySelectorAll('[data-planner-scale]').forEach(function (scale) {
      scale.removeAttribute('data-custom-ratio');
      self.paintScale(scale);
    });

    this.clearSavedState();
  };

  /*
   * Replays a saved snapshot back into the DOM and lands on the same step,
   * or just shows question 1 as before when there is nothing to replay.
   *
   * Values are written directly (checked/value) rather than through
   * emit() — emit() is how a typed edit tells the rest of the wizard about
   * itself, including the shared change listener in bind() that advances
   * past a radio question 200ms after it's picked (see VALIDATED_INPUTS).
   * Firing that here would auto-advance past whichever radio question this
   * snapshot answered, on top of the step it's already about to jump to.
   * bindScales()'s own initial paintScale() call and CSS's :checked
   * selectors pick up the direct writes just as well without it.
   */
  Planner.prototype.applyFieldSnapshot = function (data) {
    var self = this;
    /* Not named `escape` — that already exists as a deprecated global, and
       shadowing it here would read as calling that instead of this. */
    var escapeSelector = window.CSS && window.CSS.escape ? window.CSS.escape : function (v) { return v; };

    Object.keys(data.radios || {}).forEach(function (name) {
      var match = self.root.querySelector(
        'input[type="radio"][name="' + escapeSelector(name) + '"][value="' + escapeSelector(String(data.radios[name])) + '"]'
      );
      if (match) match.checked = true;
    });

    Object.keys(data.fields || {}).forEach(function (id) {
      var el = self.root.querySelector('#' + escapeSelector(id));
      if (!el) return;
      if (el.type === 'checkbox') el.checked = !!data.fields[id];
      else el.value = data.fields[id];
    });

    /* A saved session with every flavour unticked would land the visitor on
       a step they can't leave without noticing why — start from all three
       again, the same as a fresh visit. */
    var flavourBoxes = this.root.querySelectorAll('[data-planner-field="gel_flavours"]');
    var anyFlavour = Array.prototype.some.call(flavourBoxes, function (box) { return box.checked; });
    if (flavourBoxes.length && !anyFlavour) {
      Array.prototype.forEach.call(flavourBoxes, function (box) { box.checked = true; });
    }

    /* Bounds, placeholder and label follow the checked unit radio (see
       applyWeightUnit) — restoring the radio alone would leave them at
       whatever the server-rendered "kg" default was, out of step with a
       restored "lb" pick. Not passing convert: the number field's value is
       already in that same unit, so re-converting it here would corrupt it. */
    this.root.querySelectorAll('[data-planner-weight-unit]:checked').forEach(function (radio) {
      self.applyWeightUnit(radio.value, { scope: radio.closest('[data-planner-step]') || self.root });
    });

    /* Cosmetic only (which distance preset chip reads as selected) — every
       other widget's own visual state already follows the input it shadows
       without this, either through bindScales()'s initial paint or through
       plain CSS :checked. */
    this.root.querySelectorAll('[data-planner-field="distance_km"]').forEach(function (input) {
      self.syncPresets(input);
    });
  };

  Planner.prototype.restoreState = function () {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(this.storageKey());
    } catch (e) {
      raw = null;
    }

    var data = null;
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = null;
      }
    }

    if (!data || typeof data !== 'object') {
      this.show(0);
      return;
    }

    this.applyFieldSnapshot(data);

    /* A finished plan takes priority over the wizard-step target below —
       render it directly rather than replaying steps and resubmitting (see
       saveResultsState()'s comment on why resubmitting would be wrong). */
    if (data.panel === 'results' && data.plan) {
      this.plan = data.plan;
      this.lastPayload = data.lastPayload || {};
      this.renderResults(this.plan);
      this.panel('results');
      /* Reused from the wizard-step case below: stops openFromHash() from
         resetting this back to question 1 when the "-start" hash (set the
         moment the wizard was first opened, and still in the address bar)
         survives the refresh. */
      this.restoredToStep = true;
      return;
    }

    var target = typeof data.index === 'number' ? data.index : 0;
    if (target < 0 || target >= this.steps.length) target = 0;
    while (target > 0 && target < this.steps.length && this.isSkipped(this.steps[target])) {
      target = this.nextVisibleIndex(target, 1);
    }
    if (target < 0 || target >= this.steps.length) target = 0;

    this.restoredToStep = target > 0;
    if (this.restoredToStep) this.panel('wizard');
    this.show(target, { scroll: false });
  };

  Planner.prototype.panel = function (name) {
    var panels = this.root.querySelectorAll('[data-planner-panel]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-planner-panel') !== name;
    }
  };

  /* Some steps only apply to certain sports (Triathlon swaps distance +
     finish-time for three per-discipline times). A step opts out via
     data-planner-skip-when="Sport" (hide when that sport is selected) or
     data-planner-skip-unless="Sport" (hide unless that sport is selected). */
  Planner.prototype.sportValue = function () {
    var checked = this.root.querySelector('[data-planner-field="sport"]:checked');
    return checked ? checked.value : '';
  };

  /* Both attributes take a comma-separated list ("Run, Cycling"), so a step
     can be shared by more than one sport. */
  function sportList(value) {
    return String(value || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  Planner.prototype.isSkipped = function (stepEl) {
    var sport = this.sportValue();
    var skipWhen = sportList(stepEl.getAttribute('data-planner-skip-when'));
    var skipUnless = sportList(stepEl.getAttribute('data-planner-skip-unless'));
    if (skipWhen.length && skipWhen.indexOf(sport) !== -1) return true;
    if (skipUnless.length && skipUnless.indexOf(sport) === -1) return true;
    return false;
  };

  /* Finer-grained than a whole step: a block inside a shared step that only
     some sports answer (Hyrox shares the Run/Cycling step but has no distance
     to give — every Hyrox is 8 x 1 km). Hidden AND disabled, so the validator
     and collect() both pass over it rather than demanding a value nobody can
     see. */
  Planner.prototype.syncSportFields = function () {
    var sport = this.sportValue();
    this.root.querySelectorAll('[data-planner-hide-for]').forEach(function (block) {
      var hide = sportList(block.getAttribute('data-planner-hide-for')).indexOf(sport) !== -1;
      block.hidden = hide;
      block.querySelectorAll('input, select, textarea').forEach(function (input) {
        input.disabled = hide;
      });
    });
  };

  Planner.prototype.visibleSteps = function () {
    var self = this;
    return this.steps.filter(function (step) { return !self.isSkipped(step); });
  };

  /* Walks from `from` in `direction` (±1), skipping over currently-irrelevant
     steps, and returns the next valid index — or an out-of-range index if
     there isn't one (i.e. off the front/back of the wizard). */
  Planner.prototype.nextVisibleIndex = function (from, direction) {
    var i = from + direction;
    while (i >= 0 && i < this.steps.length && this.isSkipped(this.steps[i])) {
      i += direction;
    }
    return i;
  };

  /* Straight into question 1, skipping the intro panel. Used by the intro's own
     button and by the "-start" deep link. */
  Planner.prototype.open = function (opts) {
    var options = opts || {};
    this.editing = false;
    this.panel('wizard');
    /* Scrolls below, on its own terms: arriving from another page wants an
       instant jump, which show()'s smooth default would override. */
    this.show(0, { scroll: false });
    this.markOpenInUrl();
    (this.anchor || this.root).scrollIntoView({
      behavior: options.instant ? 'auto' : 'smooth',
      block: 'start'
    });
  };

  /*
   * Put the "-start" hash in the address bar however the wizard was opened, so
   * the intro button and the header button leave the visitor in the same place —
   * and so a reload returns to the questions instead of dropping back to the
   * marketing panel.
   *
   * replaceState, not pushState: this is the same view the visitor is already
   * looking at, and stacking a history entry per open would mean Back walks
   * through openings rather than leaving the page. The science tabs in this
   * theme mirror their state the same way.
   */
  Planner.prototype.markOpenInUrl = function () {
    if (!this.startHash || window.location.hash === this.startHash) return;
    if (!window.history || !window.history.replaceState) return;
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + this.startHash
    );
  };

  /*
   * Two addresses for this section:
   *
   *   #fuel-planner        scroll here and read the intro — the browser handles
   *                        it natively, no JS involved.
   *   #fuel-planner-start  open the wizard on question 1.
   *
   * The header's "Free Fuelling Planner" button uses the second: someone who
   * clicked through to the planner has already decided, and landing them on a
   * panel whose only control is another button into the same planner asks them
   * to decide twice.
   *
   * The browser cannot scroll to "-start" itself, since no element carries that
   * id, so open() does the scrolling. hashchange covers clicking the header
   * button while already on the home page, where no navigation occurs.
   */
  Planner.prototype.openFromHash = function () {
    if (!this.startHash || window.location.hash !== this.startHash) return;

    /* restoreState() already put the wizard on whichever later step a
       refresh brought back — open() would reset that straight back to
       question 1, since resetting to question 1 is its whole job. Only the
       scroll-into-view half of it still applies here. */
    if (this.restoredToStep) {
      (this.anchor || this.root).scrollIntoView({
        behavior: document.readyState !== 'complete' ? 'auto' : 'smooth',
        block: 'start'
      });
      this.holdPosition();
      return;
    }

    /* Arriving from another page: jump. A smooth scroll from the top of a long
       home page is a slow ride past every section before the planner appears,
       and the visitor asked for the planner, not the tour.
       Already on this page: glide, so the move reads as navigation rather than
       as the page having been replaced. */
    this.open({ instant: document.readyState !== 'complete' });
    this.holdPosition();
  };

  /*
   * Everything above the planner on the home page loads lazily — hero banners 2
   * and 3, the showcase art, the community grid. Each one that arrives after the
   * scroll has already landed adds its height above the planner and pushes it
   * down the page, so the visitor ends up looking at whatever now occupies that
   * scroll position instead of question 1.
   *
   * So re-assert the position once the page has finished loading — but only if
   * the visitor has not taken over in the meantime. Re-scrolling someone who has
   * started reading elsewhere, or who is already answering, would be worse than
   * the drift.
   */
  Planner.prototype.holdPosition = function () {
    /* Nothing left to load, so nothing can shift — and re-asserting here would
       cancel the smooth scroll open() just started on a same-page click. */
    if (document.readyState === 'complete') return;

    var self = this;
    var released = false;

    function release() {
      released = true;
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (type) {
        window.removeEventListener(type, release);
      });
    }

    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (type) {
      window.addEventListener(type, release, { passive: true, once: true });
    });

    function reassert() {
      if (released || self.index !== 0) return;
      if (window.location.hash !== self.startHash) return;
      (self.anchor || self.root).scrollIntoView({ behavior: 'auto', block: 'start' });
    }

    window.addEventListener(
      'load',
      function () {
        window.requestAnimationFrame(function () {
          reassert();
          release();
        });
      },
      { once: true }
    );
  };

  Planner.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-planner-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.open();
      });
    });

    window.addEventListener('hashchange', function () {
      self.openFromHash();
    });

    /*
     * Same-page clicks on any "-start" link, handled directly rather than left
     * to hashchange.
     *
     * hashchange alone was enough only while the hash stayed empty. Now that
     * opening the wizard writes the hash, a second click on the header button
     * points at the URL the visitor is already on: no navigation, no hashchange,
     * and no element carries the "-start" id for the browser to scroll to — so
     * the button would appear dead. Catching the click covers every case.
     *
     * Links to another page fall through untouched and are picked up by
     * openFromHash once that page loads.
     */
    document.addEventListener('click', function (event) {
      if (!self.startHash || !event.target || !event.target.closest) return;
      var link = event.target.closest('a[href]');
      if (!link || link.hash !== self.startHash) return;
      if (link.pathname !== window.location.pathname) return;
      event.preventDefault();
      self.open();
    });

    this.root.querySelectorAll('[data-planner-back]').forEach(function (button) {
      button.addEventListener('click', function () { self.show(self.nextVisibleIndex(self.index, -1)); });
    });

    this.root.querySelectorAll('[data-planner-next]').forEach(function (button) {
      button.addEventListener('click', function () { self.advance(); });
    });

    this.root.querySelectorAll('[data-planner-submit]').forEach(function (button) {
      button.addEventListener('click', function () { self.submit(); });
    });

    this.root.querySelectorAll('[data-planner-restart]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.resetFields();
        self.editing = false;
        self.panel('wizard');
        self.show(0);
      });
    });

    /* Selecting a choice advances automatically — expected feel for a
       one-question-per-screen flow. Back still works, and the final step waits
       for an explicit submit. */
    /*
     * Two selectors, because two different jobs.
     *
     *   data-planner-field     goes in the API payload AND is validated.
     *   data-planner-validate  is validated but never sent — the contact
     *                          step's email, which the recommendation service
     *                          has no field for and should never receive.
     *
     * collect() reads only the first of the two, which is what keeps that
     * separation real rather than a naming convention.
     */
    this.root.querySelectorAll(VALIDATED_INPUTS).forEach(function (input) {
      /* Clear this field's own complaint as it is answered — clearing the whole
         step would wipe the still-valid messages on its sibling questions. */
      if (input.type !== 'radio') {
        input.addEventListener('input', function () {
          self.clearFieldErrorFor(input);
          /* Live, on every keystroke — the button un-greys the moment the last
             blank is filled, which is what makes the gate read as guidance
             rather than as the form being broken. */
          self.updateNav();
          self.mirror(input);
        });
      }
      input.addEventListener('change', function () {
        self.clearFieldErrorFor(input);
        self.updateNav();
        self.mirror(input);
        if (input.type !== 'radio') return;
        /* A scale is dragged, and a drag passes through the values between the
           two ends: advancing on the first one touched would send the visitor
           to the next question mid-gesture. These steps wait for Next. */
        if (input.closest('[data-planner-scale]')) return;
        if (self.nextVisibleIndex(self.index, 1) >= self.steps.length) return;
        window.setTimeout(function () { self.advance(); }, 200);
      });
    });

    this.bindSpinners();
    this.bindPresets();
    this.bindTriathlonPresets();
    this.bindWeightRange();
    this.bindScales();

    /*
     * Weight unit switch. Swapping the unit has to swap the input's own bounds
     * with it — leaving min=30/max=200 while someone types pounds would reject
     * a legitimate 210 lb and silently accept 30 lb.
     *
     * Any number already entered is converted rather than cleared: switching
     * unit is how a visitor checks the equivalent figure, and wiping their entry
     * would be a strange answer to that.
     */
    this.root.querySelectorAll('[data-planner-weight-unit]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        /* Bodyweight can exist twice in the DOM (once inside the combined
           Run/Cycling step, once as its own Triathlon-only step) — scope to
           the radio's own step so toggling one never touches the other's
           hidden input/label. */
        var scope = radio.closest('[data-planner-step]') || self.root;
        self.applyWeightUnit(radio.value, { convert: true, scope: scope });
      });
    });

    /* Enter should move forward, not submit the page. advance() already
       decides between "show the next visible step" and "submit". */
    this.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      if (!event.target.matches || !event.target.matches('[data-planner-field]')) return;
      event.preventDefault();
      self.advance();
    });

    /* One delegated listener rather than wiring saveState() into every
       widget above — 'input'/'change' bubble up from all of them, including
       the synthetic ones emit() dispatches, so this catches every answer
       from one place. */
    this.root.addEventListener('input', function () { self.saveState(); });
    this.root.addEventListener('change', function () { self.saveState(); });
  };

  /*
   * Puts the top of the planner back under the header.
   *
   * Steps are swapped in place, so the scroll position does not move with them:
   * answering a tall question leaves the visitor level with its Back/Next row,
   * and the next question then renders ABOVE that line — off screen. They have
   * to scroll up to read what they were just asked.
   *
   * Scrolls the whole #fuel-planner section rather than the step, so the
   * progress bar ("Question 3 of 6") stays in view, and reuses the section's
   * own scroll-margin-top (eltane-base.css) so the sticky header does not
   * cover the heading.
   */
  Planner.prototype.scrollToTop = function () {
    var target = this.anchor || this.root;
    if (!target || !target.scrollIntoView) return;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  /*
   * What a step change uses instead of scrollToTop().
   *
   * scrollToTop() pulls the whole #fuel-planner section to the top of the
   * viewport on EVERY Next and Back. When the question is already on screen —
   * which it is for most of the flow — that is a page-wide jump the visitor did
   * not ask for: they click a button under their cursor and the entire page
   * slides. It also fights the answer they were just looking at.
   *
   * So only move when the question has actually drifted out of reach, and then
   * move by the smallest amount that fixes it — scrollBy, not scrollIntoView,
   * so there is no anchoring to the section top. If the question is comfortably
   * visible, nothing happens at all.
   */
  Planner.prototype.keepStepInView = function (step) {
    var target = step || this.steps[this.index];
    if (!target || !target.getBoundingClientRect || !window.scrollBy) return;

    /* Published by eltane-header.js — the sticky header's real measured height.
       Anything above this line is behind the header, not merely off screen. */
    var headerOffset = 0;
    if (window.getComputedStyle) {
      headerOffset =
        parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--eltane-header-offset')) || 0;
    }

    var guard = headerOffset + 16;
    var rect = target.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;

    /* Reachable as it stands: the question starts below the header and its top
       is on screen. Leave the page exactly where the visitor left it. */
    if (rect.top >= guard && rect.top <= viewport * 0.6) return;

    window.scrollBy({
      top: rect.top - guard,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  /*
   * Slides the incoming question in from the direction of travel.
   *
   * Steps are swapped by toggling `hidden`, which is instant — the wizard read
   * as a page reload rather than as movement through a flow. The class is
   * removed and re-added around a forced reflow because re-adding it in the
   * same frame is a no-op: the browser never sees the "off" state, so there is
   * nothing to transition from.
   */
  Planner.prototype.animateIn = function (step, forward) {
    if (!step || prefersReducedMotion()) return;
    step.classList.remove('is-in');
    step.classList.toggle('is-from-right', forward !== false);
    step.classList.toggle('is-from-left', forward === false);
    /* Forced reflow — the read is the point, not the value. */
    void step.offsetWidth;
    step.classList.add('is-in');
  };

  /*
   * Greys out Next/Submit until the step is answered.
   *
   * Only BLANK answers gate the button: a visitor who has typed nothing gains
   * nothing from a red line telling them so, and an inert button says it more
   * quietly. A value that is present but out of range leaves the button live,
   * so clicking it still surfaces the specific complaint through validate() —
   * "between 30 and 200" is information the disabled state cannot carry.
   */
  Planner.prototype.updateNav = function () {
    var step = this.steps[this.index];
    if (!step) return;

    var blocked = this.problemsFor(step).some(function (problem) {
      return problem.kind === 'blank';
    });

    ['[data-planner-next]', '[data-planner-submit]'].forEach(function (selector) {
      var button = step.querySelector(selector);
      if (!button) return;
      button.disabled = blocked;
      button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    });
  };

  Planner.prototype.show = function (index, options) {
    if (index < 0 || index >= this.steps.length) return;
    var settings = options || {};
    /* Which way the wizard is moving, so the incoming question slides in from
       the side it is travelling from. Read before this.index is reassigned. */
    var forward = index >= this.index;
    this.index = index;
    this.clearError();

    for (var i = 0; i < this.steps.length; i++) {
      this.steps[i].hidden = i !== index;
    }
    this.syncSportFields();

    this.animateIn(this.steps[index], forward);

    var visible = this.visibleSteps();
    var total = visible.length;
    var visibleIndex = visible.indexOf(this.steps[index]);

    var bar = this.root.querySelector('[data-planner-progress-bar]');
    if (bar) bar.style.width = ((visibleIndex + 1) / total * 100) + '%';

    /* "Step", not "Question" — the last screen asks for a name and an email,
       which is not a question about the race. */
    var text = this.root.querySelector('[data-planner-progress-text]');
    if (text) text.textContent = 'Step ' + (visibleIndex + 1) + ' of ' + total;

    var currentStep = this.steps[index];
    var stepLabel = currentStep.querySelector('[data-planner-step-label]');
    if (stepLabel) stepLabel.textContent = 'Step ' + (visibleIndex + 1);

    /* Hidden on the first step rather than disabled. A greyed-out Back is a
       control the visitor has to read and dismiss on every first impression of
       the planner, and it says nothing a missing button does not — there is
       nowhere behind question one to go. show() already refuses an index below
       zero, so nothing depends on the button being unreachable. */
    var back = currentStep.querySelector('[data-planner-back]');
    if (back) back.hidden = visibleIndex === 0;

    var next = currentStep.querySelector('[data-planner-next]');
    var submit = currentStep.querySelector('[data-planner-submit]');
    var isLast = visibleIndex === total - 1;
    if (next) next.hidden = isLast;
    if (submit) submit.hidden = !isLast;

    /* Arrived here from a results chip: every step can rebuild the plan on the
       spot. Without this, changing one answer means clicking Next through
       every remaining question to get back to the numbers. */
    if (this.editing && submit && !isLast) submit.hidden = false;

    this.updateNav();

    /* preventScroll matters here: without it the browser's own "reveal the
       focused element" scroll fights the one below and wins. */
    var focusable = Array.prototype.filter.call(
      this.steps[index].querySelectorAll(VALIDATED_INPUTS),
      function (input) { return !input.disabled; }
    )[0];
    if (focusable && focusable.type !== 'radio') focusable.focus({ preventScroll: true });

    /* open() does its own scroll for the first step — skip so the two do not
       stack on the same target. Otherwise this is a nudge only when the
       question has drifted out of reach, never a jump to the section top. */
    if (settings.scroll !== false) this.keepStepInView(this.steps[index]);

    this.saveState();
  };

  Planner.prototype.advance = function () {
    if (!this.validate()) return;
    var next = this.nextVisibleIndex(this.index, 1);
    if (next >= this.steps.length) this.submit();
    else this.show(next);
  };

  /*
   * Containers an inline message can hang under, most-preferred first. The
   * list is walked in order rather than handed to one .closest() call: a
   * single call returns the NEAREST match, which for the combined step is the
   * inner .eltane-planner__inputs flex row — the message would land beside the
   * box instead of beneath the whole question. Checking in order puts it under
   * .eltane-planner__combined-field, which is the visual unit a visitor reads
   * as "this question".
   *
   * The combined Run/Cycling step puts three questions on one screen, so a
   * single step-level error line cannot say WHICH of them is blank — the
   * message has to sit under the field it belongs to.
   */
  var FIELD_ERROR_ANCHORS = [
    '.eltane-planner__combined-field',
    '.eltane-planner__triathlon-row',
    '.eltane-planner__options',
    '.eltane-planner__inputs',
    '.eltane-planner__q-body'
  ];

  function fieldErrorAnchor(el) {
    if (!el || !el.closest) return null;
    for (var i = 0; i < FIELD_ERROR_ANCHORS.length; i++) {
      var match = el.closest(FIELD_ERROR_ANCHORS[i]);
      if (match) return match;
    }
    return null;
  }

  Planner.prototype.clearError = function () {
    var step = this.steps[this.index];
    var box = step && step.querySelector('[data-planner-error]');
    if (box) {
      box.textContent = '';
      box.hidden = true;
    }
    this.clearFieldErrors(step);
  };

  Planner.prototype.clearFieldErrors = function (step) {
    if (!step) return;
    step.querySelectorAll('.eltane-planner__field-error').forEach(function (node) {
      node.parentNode.removeChild(node);
    });
    step.querySelectorAll('[data-planner-invalid]').forEach(function (node) {
      node.removeAttribute('data-planner-invalid');
      node.removeAttribute('aria-invalid');
    });
  };

  /* Clears only the message covering this one field, so filling in the
     distance does not wipe the still-valid complaint about the weight. */
  Planner.prototype.clearFieldErrorFor = function (el) {
    var anchor = fieldErrorAnchor(el);
    if (!anchor) return;
    anchor.querySelectorAll('.eltane-planner__field-error').forEach(function (node) {
      node.parentNode.removeChild(node);
    });
    anchor.querySelectorAll('[data-planner-invalid]').forEach(function (node) {
      node.removeAttribute('data-planner-invalid');
      node.removeAttribute('aria-invalid');
    });
    if (anchor.hasAttribute('data-planner-invalid')) {
      anchor.removeAttribute('data-planner-invalid');
      anchor.removeAttribute('aria-invalid');
    }
  };

  /*
   * Marks one field and writes its message beneath. Returns false when there
   * is nowhere to put it, which is the caller's cue to fall back to the
   * step-level error line.
   */
  Planner.prototype.setFieldError = function (el, message, markEl) {
    var anchor = fieldErrorAnchor(el);
    if (!anchor) return false;

    var target = markEl || el;
    if (target && target.setAttribute) {
      target.setAttribute('data-planner-invalid', '');
      if (target.tagName === 'INPUT') target.setAttribute('aria-invalid', 'true');
    }

    /* One line per group — three blank timeclock inputs are one problem. */
    for (var i = 0; i < anchor.children.length; i++) {
      if (anchor.children[i].classList.contains('eltane-planner__field-error')) return true;
    }

    var note = document.createElement('p');
    note.className = 'eltane-planner__field-error';
    note.setAttribute('role', 'alert');
    note.textContent = message;
    anchor.appendChild(note);
    return true;
  };

  Planner.prototype.setError = function (message) {
    var box = this.steps[this.index] && this.steps[this.index].querySelector('[data-planner-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  };

  /*
   * Every problem on a step, reported without touching the DOM.
   *
   * Split out of validate() so the nav gate can ask exactly the question the
   * submit path asks, without painting complaints at someone who has not
   * reached the field yet.
   *
   *   kind 'blank'    nothing typed, nothing picked — this is what greys out
   *                   the Next button, because a message saying "fill this in"
   *                   tells a visitor less than an inert button already does.
   *   kind 'invalid'  a value is there but unusable (out of range, malformed).
   *                   The button stays live for these so the click can surface
   *                   the specific reason.
   */
  Planner.prototype.problemsFor = function (step) {
    if (!step) return [];

    /* Disabled = switched off for the current sport (see syncSportFields). */
    var inputs = Array.prototype.filter.call(step.querySelectorAll(VALIDATED_INPUTS), function (input) {
      return !input.disabled;
    });
    var radioGroups = {};

    /*
     * Every problem on the step is collected, not just the first. The combined
     * Run/Cycling step asks three questions at once: reporting only the first
     * blank one — or worse, printing every message on the single step-level
     * line under the last question — leaves a visitor hunting for which box
     * the complaint belongs to.
     */
    var problems = [];

    inputs.forEach(function (input) {
      if (input.type === 'radio') {
        if (!(input.name in radioGroups)) radioGroups[input.name] = { answered: false, input: input };
        if (input.checked) radioGroups[input.name].answered = true;
        return;
      }
      /* A checkbox's .value is "on" whether it's ticked or not, so the blank
         check below (which reads .value) never catches an unticked required
         box — native checkValidity() does catch it, but as 'invalid', which
         doesn't grey out Next (see updateNav()). An unticked required
         consent is exactly the "blank" case, not "answered but out of
         range", so it's pushed as 'blank' explicitly here instead. */
      if (input.type === 'checkbox') {
        if (input.required && !input.checked) {
          problems.push({
            kind: 'blank',
            input: input,
            message: input.validationMessage || 'Please check this box to continue.'
          });
        }
        return;
      }
      if (String(input.value).trim() === '' && input.required) {
        problems.push({
          kind: 'blank',
          input: input,
          message: input.validationMessage || 'Please fill out this field.'
        });
        return;
      }
      if (input.checkValidity && !input.checkValidity()) {
        problems.push({
          kind: 'invalid',
          input: input,
          message: input.validationMessage || 'Enter a valid value to continue.'
        });
      }
    });

    Object.keys(radioGroups).forEach(function (name) {
      if (radioGroups[name].answered) return;
      problems.push({ kind: 'blank', input: radioGroups[name].input, message: 'Pick an option to continue.' });
    });

    /*
     * Timeclocks need a rule the per-input loop above cannot express. A clock is
     * two separate number inputs and neither can be individually required: a
     * 25-minute swim leg legitimately leaves the hours empty, so `required` on
     * that field rejects a valid answer. What must be non-zero is the COMBINED
     * duration.
     *
     * Without this the triathlon step waved through three untouched clocks — the
     * greyed "0 : 25" is a placeholder, not a value, so every field was empty,
     * none was required, and an empty non-required number input passes
     * checkValidity(). The step then submitted a zero-duration race.
     */
    Array.prototype.slice
      .call(step.querySelectorAll('.eltane-planner__timeclock'))
      .forEach(function (clock) {
        /* A malformed entry inside this clock (minutes of 99, say) already has
           its own message from the loop above — don't stack a second one. */
        var alreadyFlagged = problems.some(function (problem) {
          return clock.contains(problem.input);
        });
        if (alreadyFlagged) return;

        var minutes = 0;
        clock.querySelectorAll('[data-planner-field]').forEach(function (input) {
          var value = parseInt(input.value, 10);
          if (isNaN(value) || value < 0) return;
          var field = input.getAttribute('data-planner-field') || '';
          if (/hours$/.test(field)) minutes += value * 60;
          else if (/minutes$/.test(field)) minutes += value;
        });
        if (minutes > 0) return;

        /* Name the discipline when there is one — on the triathlon step three
           identical clocks share one row each, so "Enter a time to continue" does
           not say which of them is blank. */
        var row = clock.closest('.eltane-planner__triathlon-row');
        var label = row && row.querySelector('.eltane-planner__triathlon-label');
        var name = label ? label.textContent.trim().toLowerCase() : '';
        problems.push({
          kind: 'blank',
          input: clock.querySelector('[data-planner-field]'),
          markEl: clock,
          message: name ? 'Enter a ' + name + ' time to continue.' : 'Enter a time to continue.'
        });
      });

    /* The flavour step needs at least one tick: the API rejects an empty list. */
    var flavourBoxes = step.querySelectorAll('[data-planner-field="gel_flavours"]');
    if (flavourBoxes.length) {
      var anyFlavour = Array.prototype.some.call(flavourBoxes, function (box) {
        return box.checked && !box.disabled;
      });
      if (!anyFlavour) {
        problems.push({
          kind: 'blank',
          input: flavourBoxes[0],
          message: 'Pick at least one flavour.'
        });
      }
    }

    return problems;
  };

  Planner.prototype.validate = function () {
    var self = this;
    var step = this.steps[this.index];
    this.clearFieldErrors(step);

    var problems = this.problemsFor(step);
    if (!problems.length) return true;

    /* Anything with no container to sit under falls back to the step-level
       line, so a message is never swallowed. */
    var orphaned = [];
    problems.forEach(function (problem) {
      if (!self.setFieldError(problem.input, problem.message, problem.markEl)) {
        orphaned.push(problem.message);
      }
    });
    if (orphaned.length) this.setError(orphaned[0]);

    var first = problems[0].input;
    if (first && first.focus) first.focus({ preventScroll: true });
    return false;
  };

  Planner.prototype.collect = function () {
    var data = {};
    var weightUnitValue = 'kg';

    /* Read only from steps relevant to the current sport, not the whole root:
       bodyweight exists twice in the DOM (once inside the combined
       Run/Cycling step, once as its own Triathlon-only step), and a stray
       empty value from the hidden copy would otherwise overwrite the real
       one when both share the same data-planner-field key. */
    this.visibleSteps().forEach(function (step) {
      step.querySelectorAll('[data-planner-field]').forEach(function (input) {
        if (input.disabled) return;
        var key = input.getAttribute('data-planner-field');
        if (input.type === 'radio') {
          if (input.checked) data[key] = input.value;
          return;
        }
        /* Flavour tick boxes: sent as a list of the ticked API names,
           e.g. ["Lemon", "Mango"]. */
        if (input.type === 'checkbox' && key === 'gel_flavours') {
          if (!data.gel_flavours) data.gel_flavours = [];
          if (input.checked) data.gel_flavours.push(input.value);
          return;
        }
        data[key] = input.value;
      });

      var checkedUnit = step.querySelector('[data-planner-weight-unit]:checked');
      if (checkedUnit) weightUnitValue = checkedUnit.value;
    });

    /* The API wants a single 'h:mm:ss' string per time field; the form splits
       each into hours and minutes so ultra-length events aren't capped by
       <input type="time">. */
    function pad(value) {
      return value < 10 ? '0' + value : String(value);
    }

    function combineTime(prefix) {
      var hours = parseInt(data[prefix + '_hours'], 10) || 0;
      var minutes = parseInt(data[prefix + '_minutes'], 10) || 0;
      delete data[prefix + '_hours'];
      delete data[prefix + '_minutes'];
      /* Seconds are always zero: the clock no longer asks for them, since every
         time here is a goal and nobody targets a finish to the second. The
         trailing ':00' stays so the string the API receives is the same shape
         it has always been. */
      return hours + ':' + pad(minutes) + ':00';
    }

    function minutesOf(prefix) {
      return (parseInt(data[prefix + '_hours'], 10) || 0) * 60 + (parseInt(data[prefix + '_minutes'], 10) || 0);
    }

    /* Triathlon reports three discipline times instead of a single distance +
       finish time. The API takes them as split_times (minutes per leg, with
       T1/T2 at 0 since the form does not ask for transitions) and works the
       finish time out as their sum; finish_time_hhmm is sent too so the two
       can never disagree. The format comes from the distances themselves — a
       preset chip fills exact figures, and anything edited away from them is
       a Custom race, whose distances the API then uses as given. */
    if (data.sport === 'Triathlon') {
      var splits = {
        swim: minutesOf('swim_time'),
        t1: 0,
        bike: minutesOf('cycle_time'),
        t2: 0,
        run: minutesOf('run_time')
      };
      var totalMinutes = splits.swim + splits.bike + splits.run;
      data.split_times = splits;
      data.finish_time_hhmm = Math.floor(totalMinutes / 60) + ':' + pad(totalMinutes % 60) + ':00';
      data.triathlon_format = triathlonFormat(data.swim_distance_km, data.cycle_distance_km, data.run_distance_km);
      ['swim_time', 'cycle_time', 'run_time', 'finish_time'].forEach(function (prefix) {
        delete data[prefix + '_hours'];
        delete data[prefix + '_minutes'];
      });
      delete data.distance_km;
    } else {
      data.finish_time_hhmm = combineTime('finish_time');
      /* Hyrox is always 8 x 1 km runs — the API fixes the distance itself. */
      if (data.sport === 'Hyrox') delete data.distance_km;
      delete data.swim_time_hours;
      delete data.swim_time_minutes;
      delete data.cycle_time_hours;
      delete data.cycle_time_minutes;
      delete data.run_time_hours;
      delete data.run_time_minutes;
    }

    /* Bodyweight is entered in either unit but always sent in kilograms — every
       rule downstream is written against kg. Rounded to one decimal because the
       extra precision is invented: nobody knows their weight to the gram, and a
       long float in the payload reads as false accuracy. */
    var weight = parseFloat(data.bodyweight_value);
    delete data.bodyweight_value;
    if (!isNaN(weight)) {
      data.bodyweight_kg = weightUnitValue === 'lb' ? Math.round((weight / LB_PER_KG) * 10) / 10 : weight;
    }

    return data;
  };

  Planner.prototype.weightUnit = function (scope) {
    var checked = (scope || this.root).querySelector('[data-planner-weight-unit]:checked');
    return checked ? checked.value : 'kg';
  };

  Planner.prototype.applyWeightUnit = function (unit, opts) {
    var spec = WEIGHT_UNITS[unit];
    var options = opts || {};
    var scope = options.scope || this.root;
    var input = scope.querySelector('[data-planner-weight-input]');
    if (!spec || !input) return;

    if (options.convert && String(input.value).trim() !== '') {
      var current = parseFloat(input.value);
      if (!isNaN(current)) {
        var converted = unit === 'lb' ? current * LB_PER_KG : current / LB_PER_KG;
        // Pounds to the nearest whole, kilograms to the nearest half — the same
        // granularity each unit's step already implies.
        input.value = unit === 'lb' ? String(Math.round(converted)) : String(Math.round(converted * 2) / 2);
      }
    }

    input.min = spec.min;
    input.max = spec.max;
    input.step = spec.step;
    input.placeholder = spec.placeholder;

    var label = scope.querySelector('[data-planner-weight-label]');
    if (label) label.textContent = spec.label;

    /* The slider is a second way into the same number, so it has to move with
       the unit: leaving a 30–200 track under a pounds figure would put 155 lb
       near the far right of a scale that ends at 200 lb. */
    var range = scope.querySelector('[data-planner-weight-range]');
    if (range) {
      range.min = spec.min;
      range.max = spec.max;
      range.step = spec.step;
      range.value = String(input.value).trim() !== '' ? input.value : spec.placeholder;
    }

    var minLabel = scope.querySelector('[data-planner-range-min]');
    if (minLabel) minLabel.textContent = spec.min;
    var maxLabel = scope.querySelector('[data-planner-range-max]');
    if (maxLabel) maxLabel.textContent = spec.max;

    // A value that was valid before the swap may not be now; clear any stale
    // message rather than leave the previous unit's complaint on screen.
    this.clearError();
    this.updateNav();
  };

  /* ==================================================================== input
     widgets — the chevron clock, the distance chips, the weight slider and the
     intensity scale. Each is a second face on a control the rest of the
     planner already understands: every one of them writes to a plain input and
     announces it with emit(), so validation, the nav gate and collect() carry
     on reading exactly what they read before.
     ==================================================================== */

  /* Fires the events a typed edit would, so every listener downstream — error
     clearing, the nav gate, the mirrored control — updates from one place
     rather than each widget remembering to call all three. */
  function emit(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Keeps the widgets that shadow a value in step with it after a typed edit. */
  Planner.prototype.mirror = function (input) {
    if (!input || !input.getAttribute) return;

    if (input.hasAttribute('data-planner-weight-range')) return;

    if (input.hasAttribute('data-planner-weight-input')) {
      var scope = input.closest('[data-planner-step]') || this.root;
      var range = scope.querySelector('[data-planner-weight-range]');
      if (range && String(input.value).trim() !== '') range.value = input.value;
    }

    if (input.getAttribute('data-planner-field') === 'distance_km') this.syncPresets(input);
  };

  /*
   * One step of a chevron button on the digital clock.
   *
   * Minutes carry into the paired hours field instead of stalling at 0/59:
   * pressing "down" from 1:00 borrows an hour and lands on 0:55, the way a
   * real clock counts. The carry only happens if the hours field actually
   * has room to move — at 0:00 there is no hour left to borrow, so minutes
   * simply clamp at 0 rather than wrapping to a negative hour. Hours
   * themselves have nothing to carry into, so they always just clamp.
   */
  Planner.prototype.spin = function (button) {
    var input = document.getElementById(button.getAttribute('data-planner-spin-target'));
    if (!input) return;

    var stepAttr = input.getAttribute('data-planner-spin-step');
    var step = stepAttr ? parseFloat(stepAttr) : (parseFloat(input.step) || 1);
    var min = input.min === '' ? -Infinity : parseFloat(input.min);
    var max = input.max === '' ? Infinity : parseFloat(input.max);
    var current = parseFloat(input.value);
    /* An untouched field shows a placeholder, not a value — the first press
       should land on the floor of the range rather than on NaN. */
    if (isNaN(current)) current = isFinite(min) ? min : 0;

    var down = button.getAttribute('data-planner-spin') === 'down';
    var next = current + (down ? -step : step);

    var wrapAttr = input.getAttribute('data-planner-spin-wrap');
    var wrap = wrapAttr ? parseFloat(wrapAttr) : 0;
    if (wrap && (next < min || next > max)) {
      var clock = input.closest('.eltane-planner__timeclock');
      var hourInput = clock && Array.prototype.filter.call(
        clock.querySelectorAll('.eltane-planner__timeclock-input'),
        function (el) { return el !== input; }
      )[0];

      if (hourInput) {
        var hMin = hourInput.min === '' ? -Infinity : parseFloat(hourInput.min);
        var hMax = hourInput.max === '' ? Infinity : parseFloat(hourInput.max);
        var hCurrent = parseFloat(hourInput.value);
        if (isNaN(hCurrent)) hCurrent = isFinite(hMin) ? hMin : 0;
        var hNext = hCurrent + (down ? -1 : 1);

        if (hNext >= hMin && hNext <= hMax) {
          next = down ? next + wrap : next - wrap;
          hourInput.value = String(hNext);
          emit(hourInput);
        }
      }
    }

    if (next < min) next = min;
    if (next > max) next = max;
    if (next === current && String(input.value).trim() !== '') return;

    /* Rounded because 0.1 + 0.2 is not 0.3, and a clock reading 4.300000000001
       is a worse bug than the one this line prevents. */
    input.value = String(Math.round(next * 100) / 100);
    emit(input);
  };

  /*
   * Press to step once, hold to run.
   *
   * Both `click` and `pointerdown` are bound because neither covers everything:
   * click is what a keyboard press produces, and pointerdown is the only one
   * that can start a repeat before the press ends. The `repeated` flag stops
   * the click that follows a hold from adding one more step on release.
   */
  Planner.prototype.bindSpinners = function () {
    var self = this;
    var holdTimer = null;
    var repeatTimer = null;
    var repeated = false;

    function stop() {
      if (holdTimer) window.clearTimeout(holdTimer);
      if (repeatTimer) window.clearInterval(repeatTimer);
      holdTimer = null;
      repeatTimer = null;
    }

    function spinButtonFor(event) {
      return event.target && event.target.closest ? event.target.closest('[data-planner-spin]') : null;
    }

    this.root.addEventListener('pointerdown', function (event) {
      var button = spinButtonFor(event);
      if (!button) return;
      repeated = false;
      stop();
      holdTimer = window.setTimeout(function () {
        repeated = true;
        repeatTimer = window.setInterval(function () { self.spin(button); }, 110);
      }, 420);
    });

    ['pointerup', 'pointercancel'].forEach(function (type) {
      document.addEventListener(type, stop);
    });

    this.root.addEventListener('click', function (event) {
      var button = spinButtonFor(event);
      if (!button) return;
      event.preventDefault();
      if (repeated) {
        repeated = false;
        return;
      }
      self.spin(button);
    });
  };

  /* Distance chips. They fill the number field rather than replacing it, so an
     odd distance is still typed and the chips only ever save keystrokes. */
  Planner.prototype.bindPresets = function () {
    var self = this;
    this.root.querySelectorAll('[data-planner-preset]').forEach(function (button) {
      button.addEventListener('click', function () {
        var input = document.getElementById(button.getAttribute('data-planner-preset-target'));
        if (!input) return;
        input.value = button.getAttribute('data-planner-preset');
        emit(input);
        input.focus({ preventScroll: true });
      });
    });
  };

  /*
   * The triathlon category row (Sprint/Olympic/Half/Full) — one click fills
   * all three distance fields at once, unlike bindPresets() above which
   * targets a single field by id. Scoped to data-planner-field rather than
   * an id: swim/cycle/run distance each exist only once per step, so the
   * field name alone is enough to find the right input without also
   * threading three target ids through the button's markup.
   */
  Planner.prototype.bindTriathlonPresets = function () {
    var self = this;
    this.root.querySelectorAll('[data-planner-tri-preset]').forEach(function (button) {
      button.addEventListener('click', function () {
        var step = button.closest('[data-planner-step]') || self.root;
        [
          ['swim_distance_km', 'data-planner-tri-swim'],
          ['cycle_distance_km', 'data-planner-tri-cycle'],
          ['run_distance_km', 'data-planner-tri-run']
        ].forEach(function (pair) {
          var input = step.querySelector('[data-planner-field="' + pair[0] + '"]');
          var value = button.getAttribute(pair[1]);
          if (!input || value === null) return;
          input.value = value;
          emit(input);
        });
      });
    });
  };

  /* Lights the chip matching whatever is in the field, however it got there —
     a typed 42.2 marks the Marathon chip too. */
  Planner.prototype.syncPresets = function (input) {
    var scope = input.closest('.eltane-planner__combined-field') || this.root;
    var current = parseFloat(input.value);
    scope.querySelectorAll('[data-planner-preset]').forEach(function (button) {
      var matches = parseFloat(button.getAttribute('data-planner-preset')) === current;
      button.classList.toggle('is-active', matches);
      button.setAttribute('aria-pressed', matches ? 'true' : 'false');
    });
  };

  Planner.prototype.bindWeightRange = function () {
    var self = this;
    this.root.querySelectorAll('[data-planner-weight-range]').forEach(function (range) {
      range.addEventListener('input', function () {
        var scope = range.closest('[data-planner-step]') || self.root;
        var input = scope.querySelector('[data-planner-weight-input]');
        if (!input) return;
        input.value = range.value;
        /* emit() rather than a direct call: dragging the slider has to clear a
           standing error and re-check the nav gate exactly as typing does. */
        emit(input);
      });
    });
  };

  /* ------------------------------------------------------------------ scale */

  /* The curve the scale is drawn from, as its four control points. Kept in
     step with the `d` attribute in snippets/eltane-planner-scale.liquid — the
     knob is placed by evaluating this, so a curve edited in one place and not
     the other would leave the knob floating off the line. */
  var SCALE_CURVE = {
    x: [0, 110, 190, 300],
    y: [98, 96, 74, 2]
  };

  function bezierAt(points, t) {
    var u = 1 - t;
    return (
      u * u * u * points[0] +
      3 * u * u * t * points[1] +
      3 * u * t * t * points[2] +
      t * t * t * points[3]
    );
  }

  /*
   * Paints the graph from whichever radio is checked: the fill clipped to the
   * selected width, the knob sitting on the curve at that point, and the hint
   * line describing it.
   */
  Planner.prototype.paintScale = function (scale, explicitRatio) {
    var inputs = Array.prototype.slice.call(scale.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;

    var index = 0;
    var anyChecked = false;
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) {
        index = i;
        anyChecked = true;
      }
    }

    var ratio;
    if (typeof explicitRatio !== 'undefined') {
      ratio = explicitRatio;
    } else if (scale.hasAttribute('data-custom-ratio')) {
      ratio = parseFloat(scale.getAttribute('data-custom-ratio'));
    } else {
      ratio = inputs.length > 1 ? index / (inputs.length - 1) : 0;
    }

    var x = bezierAt(SCALE_CURVE.x, ratio);
    var y = bezierAt(SCALE_CURVE.y, ratio);

    var clip = scale.querySelector('[data-planner-scale-clip]');
    /* A floor on the fill: at the lowest stop the true width is zero, and a
       graph with no colour at all reads as "nothing selected" rather than as
       "the easy end". */
    if (clip) {
      clip.style.width = (x / 3) + '%';
    }

    var knob = scale.querySelector('[data-planner-scale-knob]');
    if (knob) {
      /* The viewBox is 300 x 100, so a user unit converts to a percentage of
         the box by dividing x by three and leaving y as it is. */
      knob.style.left = x / 3 + '%';
      knob.style.top = y + '%';
    }

    var closestIndex = Math.round(ratio * (inputs.length - 1));

    /* Suppressed until something is actually checked (or being dragged into
       place): showing "Conversational the whole way…" for a stop nobody
       picked reads as an answer the visitor never gave. Live during a drag
       (explicitRatio is set) since the preview has to track the pointer. */
    var hint = scale.querySelector('[data-planner-scale-hint-text]');
    if (hint) {
      hint.textContent = anyChecked || typeof explicitRatio !== 'undefined'
        ? inputs[closestIndex].getAttribute('data-planner-scale-hint') || ''
        : '';
    }

    scale.setAttribute('data-planner-scale-index', String(closestIndex));
  };

  Planner.prototype.bindScales = function () {
    var self = this;

    this.root.querySelectorAll('[data-planner-scale]').forEach(function (scale) {
      var inputs = Array.prototype.slice.call(scale.querySelectorAll('input[type="radio"]'));
      var graph = scale.querySelector('[data-planner-scale-graph]');

      inputs.forEach(function (input) {
        input.addEventListener('change', function () { 
          if (!scale.isDropping) scale.removeAttribute('data-custom-ratio');
          self.paintScale(scale); 
        });
        /* Focus follows the keyboard around a radio group without necessarily
           changing the selection in every browser; repaint either way. */
        input.addEventListener('focus', function () { 
          if (!scale.isDropping) scale.removeAttribute('data-custom-ratio');
          self.paintScale(scale); 
        });
      });

      if (graph && inputs.length > 1) {
        var dragging = false;

        function pick(event, snap) {
          var rect = graph.getBoundingClientRect();
          if (!rect.width) return;
          var ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
          
          if (snap) {
            var index = Math.round(ratio * (inputs.length - 1));
            scale.setAttribute('data-custom-ratio', String(ratio));
            scale.isDropping = true;
            if (!inputs[index].checked) {
              inputs[index].checked = true;
              emit(inputs[index]);
            } else {
              self.paintScale(scale);
            }
            scale.isDropping = false;
          } else {
            self.paintScale(scale, ratio);
          }
        }

        graph.addEventListener('pointerdown', function (event) {
          dragging = true;
          scale.classList.add('is-dragging');
          if (graph.setPointerCapture) graph.setPointerCapture(event.pointerId);
          /* Stops the press from being read as the start of a text selection,
             which on a slow drag leaves the whole step highlighted blue. */
          event.preventDefault();
          pick(event, false);
        });

        graph.addEventListener('pointermove', function (event) {
          if (dragging) pick(event, false);
        });

        ['pointerup', 'pointercancel'].forEach(function (type) {
          graph.addEventListener(type, function (event) {
            if (dragging) {
              dragging = false;
              scale.classList.remove('is-dragging');
              pick(event, true);
            }
          });
        });
      }

      self.paintScale(scale);
    });
  };

  /* Read out while the request is in flight, so the wait reads as work being
     done rather than as a stalled spinner. Purely cosmetic — the request is
     not staged, and the sequence stops the moment a response lands. */
  var LOADING_LINES = [
    'Reading your pace…',
    'Setting your carbohydrate target…',
    'Timing every gel…',
    'Matching fluids to the conditions…',
    'Choosing your flavours…'
  ];

  Planner.prototype.narrateLoading = function () {
    var node = this.root.querySelector('[data-planner-loading-text]');
    if (!node) return;
    this.stopNarration();

    var line = 0;
    node.textContent = LOADING_LINES[0];
    this.narrationTimer = window.setInterval(function () {
      line += 1;
      /* Hold on the last line rather than looping: a repeating sequence tells
         the visitor the wait has no end. */
      if (line >= LOADING_LINES.length) return;
      node.textContent = LOADING_LINES[line];
    }, 900);
  };

  Planner.prototype.stopNarration = function () {
    if (this.narrationTimer) window.clearInterval(this.narrationTimer);
    this.narrationTimer = null;
  };

  Planner.prototype.submit = function () {
    if (!this.validate()) return;
    var self = this;
    var payload = this.collect();

    /* The API's "calculated" block doesn't necessarily echo every input back —
       falling back to what was actually submitted lets the results panel show
       Distance/Bodyweight/Intensity/Climate even when it doesn't. */
    this.lastPayload = payload;

    this.panel('loading');
    this.narrateLoading();
    /* Same in-place swap as a step change: submitting from the bottom of the
       last question would otherwise leave the spinner, and then the plan,
       scrolled past. */
    this.scrollToTop();

    window
      .fetch(this.apiBase + '/api/recommend', {
        method: 'POST',
        mode: 'cors',
        headers: this.apiHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
        body: JSON.stringify(apiPayload(payload))
      })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.error || 'API returned ' + response.status);
          return body;
        });
      })
      .then(function (plan) {
        self.stopNarration();
        self.plan = plan;
        self.renderResults(plan);
        self.panel('results');
        /* The plan itself is now what gets persisted (see saveResultsState)
           rather than the wizard's in-progress answers — a refresh here
           should show the same finished plan back, not send the visitor
           through the questions again. */
        self.saveResultsState();
        /* After the panel is up, never before: the plan is the thing the
           visitor asked for, and the address is housekeeping that must not be
           able to delay it or break it. */
        self.deliverPlan(plan);
      })
      .catch(function (error) {
        self.stopNarration();
        self.renderError(error);
        self.panel('error');
      });
  };

  Planner.prototype.renderError = function (error) {
    var box = this.root.querySelector('[data-planner-error-message]');
    if (!box) return;
    box.textContent = '';

    box.appendChild(el('p', 'eltane-planner__error-text', error.message || 'Could not reach the planner service.'));

    /* The single most likely cause in production: an HTTPS storefront cannot
       call an http:// API. Say so instead of leaving a bare network error. */
    if (window.location.protocol === 'https:' && this.apiBase.indexOf('http://') === 0) {
      box.appendChild(
        el(
          'p',
          'eltane-planner__error-hint',
          'This page is served over HTTPS but the planner API is configured as ' +
            this.apiBase +
            '. Browsers block mixed content. Expose the API over HTTPS (e.g. an ngrok tunnel) and update the API base URL in the section settings.'
        )
      );
    } else {
      box.appendChild(
        el('p', 'eltane-planner__error-hint', 'Confirm the API is running at ' + this.apiBase + ' and that CORS allows this origin.')
      );
    }
  };

  /* Sport-specific heading for the results panel. */
  var SPORT_RESULT_HEADING = {
    Run: 'Your Race Fuel Planner',
    Cycling: 'Your Cycling Fuel Planner',
    Triathlon: 'Your Triathlon Fuel Planner',
    Hyrox: 'Your Hyrox Fuel Planner'
  };

  /* A small pencil, drawn inline so the summary chips do not depend on an icon
     sprite that this section does not otherwise load. */
  function pencilIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M4 20h4L19 9a2.1 2.1 0 0 0 -3 -3L5 17v3z M13.5 6.5l3 3');
    svg.appendChild(path);
    return svg;
  }

  /*
   * Small line icons for the summary chips and the per-hour hero stats —
   * same minimal stroke language as pencilIcon() above (24x24, round caps,
   * currentColor), drawn inline for the same reason: no icon sprite this
   * section would otherwise have to load just for these.
   */
  function lineIcon(paths) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    paths.forEach(function (d) {
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      svg.appendChild(path);
    });
    return svg;
  }

  /* Keyed by the same field name summaryPairs() already tags each chip with,
     so summaryChip() can look one up without a second, parallel list to keep
     in step with it. */
  var CHIP_ICONS = {
    sport: ['M4 12h3l2 -5 4 10 2 -5h5'],
    distance_km: ['M12 21s-6 -5.69 -6 -10a6 6 0 1 1 12 0c0 4.31 -6 10 -6 10z', 'M12 13a2 2 0 1 0 0 -4 2 2 0 0 0 0 4z'],
    finish_time_hours: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0 -18z', 'M12 7.5v5l3.5 2'],
    swim_time_hours: ['M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0 -18z', 'M12 7.5v5l3.5 2'],
    swim_distance_km: ['M12 21s-6 -5.69 -6 -10a6 6 0 1 1 12 0c0 4.31 -6 10 -6 10z', 'M12 13a2 2 0 1 0 0 -4 2 2 0 0 0 0 4z'],
    caffeine_use: ['M5 8h11v6a5 5 0 0 1 -5 5h-1a5 5 0 0 1 -5 -5z', 'M16 10h1.5a2.5 2.5 0 0 1 0 5h-1.5', 'M9 3v2', 'M12 3v2'],
    intensity: ['M6 17v-4', 'M12 17v-9', 'M18 17v-6'],
    gel_flavours: ['M12 3c4 4.5 6 7.5 6 10.5a6 6 0 0 1 -12 0c0 -3 2 -6 6 -10.5z'],
    climate: ['M12 3v2', 'M12 19v2', 'M4.2 4.2l1.4 1.4', 'M18.4 18.4l1.4 1.4', 'M3 12h2', 'M19 12h2', 'M4.2 19.8l1.4 -1.4', 'M18.4 5.6l1.4 -1.4', 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0 -8z'],
    bodyweight_value: ['M4 10v4', 'M20 10v4', 'M2 11v2', 'M22 11v2', 'M6 12h12']
  };

  function chipIcon(field) {
    var paths = CHIP_ICONS[field];
    return paths ? lineIcon(paths) : null;
  }

  var FLAME_ICON = ['M12 12c2 -2.96 0 -7 -1 -8 0 3.038 -1.773 4.741 -3 6 -1.226 1.26 -2 3.24 -2 5a6 6 0 1 0 12 0c0 -1.532 -1.056 -3.94 -2 -5 -1.786 3 -3.671 4.766 -4 2z'];
  var BOLT_ICON = ['M13 2 3 14h9l-1 8 10-12h-9l1-8z'];
  var DROP_ICON = ['M12 3s-6 6.5 -6 11a6 6 0 0 0 12 0c0 -4.5 -6 -11 -6 -11z'];

  /* One-line drinking instruction under the Fluids figure: a sip schedule
     for steady-state sports, a per-segment amount for Hyrox (no drinking
     during station work). */
  function fluidHeadline(fluids) {
    if (fluids.sip_ml) return 'Sip ' + fluids.sip_ml + ' ml every ' + fluids.sip_every_min + ' min';
    if (fluids.per_run_segment_ml) return fluids.per_run_segment_ml + ' ml per run segment';
    return '';
  }

  /* 'pickle_pre_race' is the type plans saved before spec v2.1 used. */
  function eventKind(event) {
    if (event.type === 'pickle' || event.type === 'pickle_pre_race') return 'pickle';
    if (event.type === 'food') return 'food';
    return event.flavour === 'coffee' ? 'coffee' : 'gel';
  }

  /* "Gel · Lemon Lime", "Gel · Classic Coffee (Caffeinated) · 75 mg", "Pickle juice shot · 75 ml",
     "Food · 1 portion (25 g carbs)". Shared by the results timeline and the
     downloadable image. */
  function eventTitle(event) {
    var kind = eventKind(event);
    if (kind === 'pickle') {
      return 'Pickle juice shot' + (event.ml ? ' · ' + event.ml + ' ml' : '');
    }
    if (kind === 'food') {
      return 'Food · 1 portion (' + (event.portion_g || 25) + ' g carbs)';
    }
    if (event.flavour === 'coffee') {
      return 'Gel · Classic Coffee (Caffeinated)' + (event.caffeine_mg ? ' · ' + event.caffeine_mg + ' mg' : '');
    }
    var flavour = event.product_flavour ? FLAVOR_LABEL[event.product_flavour] || event.product_flavour : '';
    return flavour ? 'Gel · ' + flavour : 'Gel';
  }

  /*
   * One of the two headline "per hour" stats — see renderResults()'s hero
   * block. Icon + label on top, the number itself sized like a real
   * headline (bigger than a regular tile's), and an optional supporting
   * line under it, e.g. "45 g total across the event" — always derived
   * from a figure the API actually returned, never invented copy.
   */
  function heroStat(iconPaths, label, value, unit, detail, index) {
    var wrap = el('div', 'eltane-planner__hero-stat');

    var head = el('div', 'eltane-planner__hero-stat-head');
    var icon = lineIcon(iconPaths);
    icon.classList.add('eltane-planner__hero-stat-icon');
    head.appendChild(icon);
    head.appendChild(el('span', 'eltane-planner__hero-stat-label', label));
    wrap.appendChild(head);

    var val = el('p', 'eltane-planner__hero-stat-value');
    val.appendChild(el('span', 'eltane-planner__tile-num', value));
    if (unit) val.appendChild(el('span', 'eltane-planner__hero-stat-unit', unit));
    wrap.appendChild(val);

    if (detail) wrap.appendChild(el('p', 'eltane-planner__hero-stat-detail', detail));
    return reveal(wrap, index || 0);
  }

  /*
   * Sends the visitor back to the step that owns one answer.
   *
   * The summary chips exist so a plan can be adjusted without starting again:
   * seeing "Mild" next to a number that looks wrong is the moment someone
   * wants to change it, and "Start over" makes them re-enter six answers to
   * fix one. Steps skipped by the current sport are passed over, so the chip
   * cannot land on the hidden Triathlon copy of bodyweight.
   */
  Planner.prototype.editField = function (field) {
    for (var i = 0; i < this.steps.length; i++) {
      if (this.isSkipped(this.steps[i])) continue;
      if (!this.steps[i].querySelector('[data-planner-field="' + field + '"]')) continue;
      this.editing = true;
      this.panel('wizard');
      this.show(i);
      return;
    }
  };

  Planner.prototype.summaryChip = function (label, value, field, order) {
    var self = this;
    var chip = el('button', 'eltane-planner__chip');
    chip.type = 'button';

    var icon = chipIcon(field);
    if (icon) {
      icon.classList.add('eltane-planner__chip-icon');
      chip.appendChild(icon);
    }

    /* Value first, label underneath: the answer itself is what a visitor
       scanning this row actually reads for, the field name is just there to
       disambiguate it — same order the per-hour hero stats use below. */
    var stack = el('span', 'eltane-planner__chip-stack');
    stack.appendChild(el('span', 'eltane-planner__chip-value', value));
    stack.appendChild(el('span', 'eltane-planner__chip-label', label));
    chip.appendChild(stack);

    /* No question behind it (Hyrox's fixed "Indoor"): show it, don't offer an edit. */
    if (!field) {
      chip.disabled = true;
      return reveal(chip, order);
    }

    var pencil = pencilIcon();
    pencil.classList.add('eltane-planner__chip-edit');
    chip.appendChild(pencil);

    chip.setAttribute('aria-label', 'Edit ' + label.toLowerCase() + ' — currently ' + value);
    chip.addEventListener('click', function () { self.editField(field); });
    return reveal(chip, order);
  };

  /*
   * The answer row above the numbers: what the plan was built from, each one a
   * button back to its own question.
   *
   * Values come from the payload that was actually sent rather than from the
   * inputs, so what is shown is what the calculation used — the two diverge
   * the moment someone edits a field and does not resubmit.
   */
  Planner.prototype.summaryPairs = function (calc, weather) {
    /* this.lastPayload, not this.answers — the latter is set once at
       construction and never written to again, so it was always {} here. */
    var answers = this.lastPayload || {};
    var sport = this.sportValue();
    var chips = [];

    if (sport) chips.push(['Sport', sport, 'sport']);
    if (sport === 'Triathlon' && answers.triathlon_format) {
      chips.push(['Format', answers.triathlon_format, 'swim_distance_km']);
    } else if (sport !== 'Triathlon' && answers.distance_km) {
      chips.push(['Distance', answers.distance_km + ' km', 'distance_km']);
    }
    if (calc.duration_hours !== undefined && calc.duration_hours !== null) {
      chips.push([
        'Duration',
        calc.duration_hours + ' hr',
        sport === 'Triathlon' ? 'swim_time_hours' : 'finish_time_hours'
      ]);
    }
    if (answers.intensity) chips.push(['Effort', answers.intensity, 'intensity']);

    /* Hyrox is indoors and never asks, so its chip is not editable. */
    var conditions = climateLabel(weather);
    if (conditions) chips.push(['Conditions', conditions, weather.indoor ? null : 'climate']);

    if (answers.bodyweight_kg) chips.push(['Weight', answers.bodyweight_kg + ' kg', 'bodyweight_value']);
    if (CAFFEINE_USE_LABEL[answers.caffeine_use]) {
      chips.push(['Caffeine', CAFFEINE_USE_LABEL[answers.caffeine_use], 'caffeine_use']);
    }
    if (answers.gel_flavours && answers.gel_flavours.length) {
      var flavourNames = answers.gel_flavours.map(function (key) { return FLAVOR_LABEL[key] || key; });
      chips.push(['Flavours', flavourNames.join(', '), 'gel_flavours']);
    }

    return chips;
  };

  Planner.prototype.renderSummary = function (calc, weather) {
    var chips = this.summaryPairs(calc, weather);
    if (!chips.length) return null;

    var row = el('div', 'eltane-planner__summary');
    for (var i = 0; i < chips.length; i++) {
      row.appendChild(this.summaryChip(chips[i][0], chips[i][1], chips[i][2], i));
    }
    return row;
  };

  /*
   * The optional first name from question 1.
   *
   * Scrubbed rather than trusted: this string is painted onto a canvas and
   * pushed into a download filename, so anything that is not name-shaped —
   * newlines, slashes, control characters — is dropped rather than escaped.
   * The length cap is what the plan image can lay out on one line.
   */
  Planner.prototype.plannerName = function () {
    var input = this.root.querySelector('[data-planner-name]');
    if (!input) return '';
    var value = String(input.value || '').trim();
    if (!value) return '';
    try {
      value = value.replace(/[^\p{L}\p{M}'’\- ]/gu, '');
    } catch (error) {
      /* Older engines without Unicode property escapes — fall back to a
         conservative ASCII-only filter rather than leaving the value raw. */
      value = value.replace(/[^A-Za-z'’\- ]/g, '');
    }
    return value.replace(/\s+/g, ' ').trim().slice(0, 24);
  };

  /* Only ever returns an address the browser itself accepted — the input is
     type="email", so checkValidity() is the same test the step was gated on. */
  Planner.prototype.plannerEmail = function () {
    var input = this.root.querySelector('[data-planner-email]');
    if (!input) return '';
    var value = String(input.value || '').trim();
    if (!value) return '';
    if (input.checkValidity && !input.checkValidity()) return '';
    return value;
  };

  /*
   * Two separate consents, two separate questions:
   *
   *   dataConsentGiven      — required. Consent to use the submitted answers
   *                           to build the plan and email it over, plus the
   *                           age/parental gate. Enforced in problemsFor()'s
   *                           checkbox branch, not here. There is no separate
   *                           plan-by-email consent any more — sending the
   *                           plan the visitor just asked for is part of
   *                           fulfilling that request, not a marketing email,
   *                           so it rides on this same required checkbox (see
   *                           deliverPlan()).
   *   marketingConsentGiven — optional. Whether the address joins the
   *                           marketing list. Replaces the old single
   *                           consentGiven().
   */
  Planner.prototype.dataConsentGiven = function () {
    var box = this.root.querySelector('[data-planner-consent-data]');
    return !!(box && box.checked);
  };

  Planner.prototype.marketingConsentGiven = function () {
    var box = this.root.querySelector('[data-planner-consent-marketing]');
    return !!(box && box.checked);
  };

  /*
   * What happens to the address after the plan is on screen.
   *
   * Two separate things, deliberately not conflated:
   *
   *   The marketing list. Only ever on a ticked marketing consent box, and it
   *   goes to Shopify's own customer endpoint so the subscriber lands in the
   *   store's customer list rather than somewhere only this section knows
   *   about. Independent of whether the plan itself gets emailed — a visitor
   *   can join the list without asking for a mailed copy of this plan.
   *
   *   The plan email. Authorised by the required data-use consent already
   *   ticked to reach this point (see dataConsentGiven) — there's no separate
   *   plan-by-email checkbox; a visitor who typed an address to build their
   *   plan gets that same plan mailed to it. A Shopify theme cannot send mail
   *   itself, so this posts the plan to whatever service the merchant names
   *   in the section settings. With no endpoint configured, nothing claims
   *   to have emailed anything — saying "check your inbox" when no mail was
   *   sent is worse than saying nothing.
   */
  Planner.prototype.deliverPlan = function (plan) {
    var self = this;
    var email = this.plannerEmail();
    if (!email) return;

    var marketingGiven = this.marketingConsentGiven();
    if (marketingGiven) this.subscribeCustomer(email);

    var status = this.root.querySelector('[data-planner-contact-status]');
    function report(message) {
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }

    /* Marketing signup above is silent either way — no confirmation line
       when there's no endpoint configured to actually mail the plan to. */
    if (!this.leadEndpoint) return;

    report('Sending your plan to ' + email + '…');

    /* Same PNG the "Download my plan" button renders, so the email carries
       something to keep rather than just a paragraph. Best-effort: a canvas
       failure here must not stop the plan itself from being emailed — worst
       case the recipient just gets the text version. */
    var fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    fontsReady
      .catch(function () {})
      .then(function () {
        var planImage = null;
        try {
          var canvas = self.drawPlanImage();
          if (canvas && canvas.toDataURL) planImage = canvas.toDataURL('image/png');
        } catch (error) {
          planImage = null;
        }

        return window.fetch(self.leadEndpoint, {
          method: 'POST',
          mode: 'cors',
          headers: self.apiHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
          body: JSON.stringify({
            email: email,
            name: self.plannerName(),
            consent: marketingGiven,
            planEmailConsent: true,
            answers: self.lastPayload || {},
            plan: plan,
            planImage: planImage
          })
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('Endpoint returned ' + response.status);
        report('Plan sent to ' + email + '.');
      })
      .catch(function () {
        /* The plan is already on screen, so a failed email is a footnote and
           not a failure worth reporting. Clear the "Sending…" line rather than
           replacing it with an apology — an empty message hides the element
           (see report()), which leaves the panel reading as the success it is.
           The download button is already there for anyone who wants a copy. */
        report('');
      });
  };

  Planner.prototype.subscribeCustomer = function (email) {
    /* Shopify's customer form takes multipart form data, not JSON — the same
       shape the theme's newsletter block posts. The response is a rendered
       page, so there is nothing useful to read back; a failure here must not
       touch the plan the visitor is looking at. */
    var body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[first_name]', this.plannerName());
    body.append('contact[tags]', 'newsletter, fuel-planner');

    window.fetch(this.contactUrl, { method: 'POST', body: body }).catch(function () {});
  };

  Planner.prototype.renderResults = function (plan) {
    var host = this.root.querySelector('[data-planner-results]');
    if (!host) return;
    host.textContent = '';

    var carb = plan.carb_plan || {};
    var sodium = plan.sodium_plan || {};
    var cramp = plan.cramp_plan || {};
    var calc = plan.calculated || {};
    var submitted = this.lastPayload || {};
    /* { indoor, climate, climate_label, humidity_high } — the climate the
       visitor picked (or Hyrox's fixed indoor baseline). */
    var weather = plan.weather || {};

    /* Two columns on desktop: the plan reads down the left while the pack it
       maps to stays pinned on the right, so the buy decision is never scrolled
       away from the reason for it. */
    var layout = el('div', 'eltane-planner__result');
    var main = el('div', 'eltane-planner__result-main');
    var aside = el('aside', 'eltane-planner__result-aside');
    layout.appendChild(main);
    layout.appendChild(aside);
    host.appendChild(layout);

    var heading = SPORT_RESULT_HEADING[this.sportValue()] || 'Your Fuel Planner';
    var name = this.plannerName();

    /* h2 under the page's h1, matching the question titles this panel replaces
       — the results are a sibling of the questions, not a child of them. */
    main.appendChild(el('h2', 'eltane-planner__result-title', name ? 'Hey ' + name + ',' : heading));

    var summary = this.renderSummary(calc, weather);
    if (summary) {
      main.appendChild(
        el(
          'p',
          'eltane-planner__result-sub',
          (name ? heading : 'Built from your answers') + ' — tap any answer below to change it.'
        )
      );
      main.appendChild(summary);
    }

    /* Download sits at the top so the plan can be saved without scrolling
       past the whole timeline. */
    if (this.allowDownload) main.appendChild(this.renderDownload());

    /* Prefer whatever the API echoes back in "calculated" — it's proof the
       input was actually read — and fall back to what was submitted so the
       tag still renders if the API doesn't echo a given field. */
    var intensity = calc.intensity || submitted.intensity;
    var climate = climateLabel(weather);
    if (intensity || climate) {
      var basis = el('p', 'eltane-planner__flavors eltane-planner__result-basis');
      basis.appendChild(el('span', 'eltane-planner__flavors-label', 'Plan basis'));
      basis.appendChild(el('span', null, [intensity, climate].filter(Boolean).join(' · ')));
      /* Into the plan column, not the outer host: appended to host it fell
         below the whole layout, under the Race Day Pack on phones. */
      main.appendChild(basis);
    }

    /* ---- the planner's own figures.
       summary / fluids / sodium_check come from the API's Smart Fuelling
       Planner; carb_plan / sodium_plan are the older keys it still sends.
       Each figure falls back to the older key so a plan saved before the
       planner existed (see restoreState) still renders rather than blanking. */
    var totals = plan.summary || {};
    var fluids = plan.fluids || {};
    var sodiumCheck = plan.sodium || plan.sodium_check || {};
    var pickle = plan.pickle || {};
    var servings = sodiumCheck.electrolyte_servings || 0;

    var carbsPerHour = totals.carbs_g_per_h !== undefined ? totals.carbs_g_per_h : carb.target_carbs_hr_g;
    var carbsTotal = totals.carbs_g !== undefined ? totals.carbs_g : carb.total_carbs_g;
    var sodiumPerHour = totals.sodium_mg_per_h !== undefined ? totals.sodium_mg_per_h : sodium.sodium_from_gels_hr_mg;
    var sodiumRange = sodiumCheck.target_range_mg_per_h || sodium.target_range_hr_mg;
    /* Under an hour there is no in-race fuelling, so a per-hour sodium rate
       from one pre-start gel is not a figure to hold up against a target. */
    var sodiumRated = sodiumCheck.status !== 'not_applicable' && sodiumCheck.status !== 'no_gels';

    if (carbsPerHour !== undefined || fluids.ml_per_h !== undefined) {
      main.appendChild(el('p', 'eltane-planner__result-lead', 'Your race, per hour'));

      var hero = el('div', 'eltane-planner__hero eltane-planner__hero--3');
      hero.appendChild(
        heroStat(
          FLAME_ICON,
          'Carbs',
          num(carbsPerHour),
          'g / hr',
          carbsTotal !== undefined ? carbsTotal + ' g total across the event' : '',
          0
        )
      );
      if (fluids.ml_per_h !== undefined) {
        hero.appendChild(heroStat(DROP_ICON, 'Fluids', fluids.ml_per_h, 'ml / hr', fluidHeadline(fluids), 1));
      }
      var sodiumDetail = sodiumRange && sodiumRated ? 'Target ' + sodiumRange[0] + '–' + sodiumRange[1] + ' mg / hr in this heat' : '';
      if (servings) {
        sodiumDetail += (sodiumDetail ? ' · ' : '') + 'incl. ' + servings + ' electrolyte serving' + (servings === 1 ? '' : 's');
      }
      hero.appendChild(heroStat(BOLT_ICON, 'Sodium', num(sodiumPerHour), 'mg / hr', sodiumDetail, 2));
      main.appendChild(hero);
    }

    /* Distance, Duration and Bodyweight are not repeated here — the answer
       chips above (see renderSummary/summaryPairs) already show all three,
       tap-to-edit and all. Everything below is either derived from those
       answers (pace, speed) or new information the chips don't carry. */
    var tiles = el('div', 'eltane-planner__tiles');
    /* Triathlon has no single pace — three legs, three different units — so
       the API returns them separately as calc.leg_paces. Cycling reads as a
       speed rather than a pace, and Hyrox (runs broken up by stations) has
       neither, so it simply goes without. */
    if (calc.leg_paces) {
      tiles.appendChild(tile('Swim pace', num(calc.leg_paces.swim_pace, '—')));
      if (calc.leg_paces.cycle_speed_kmh) {
        tiles.appendChild(tile('Bike speed', calc.leg_paces.cycle_speed_kmh, 'km/h'));
      } else {
        tiles.appendChild(tile('Cycle pace', num(calc.leg_paces.cycle_pace, '—')));
      }
      tiles.appendChild(tile('Run pace', num(calc.leg_paces.run_pace, '—')));
    } else if (calc.speed_kmh) {
      tiles.appendChild(tile('Avg speed', calc.speed_kmh, 'km/h'));
    } else if (calc.pace) {
      tiles.appendChild(tile('Avg pace', calc.pace));
    }
    tiles.appendChild(tile('Total carbs', num(carbsTotal), 'g'));

    /* Since spec v2.1 the planner sets the gel count itself, so the pack and
       the timeline always agree. Older saved plans only have carb_plan. */
    var gelCount = totals.gels !== undefined ? totals.gels : carb.qty_gels;
    var carry = plan.carry_list || {};
    var gelDetail = carry.pre_start_gels ? carry.pre_start_gels + ' before the start' : '';
    tiles.appendChild(tile('Performance gels', num(gelCount), null, gelDetail));

    /* Caffeine is the one number a customer may need to check deliberately. */
    if (totals.caffeine_mg !== undefined) {
      tiles.appendChild(
        tile('Caffeine', totals.caffeine_mg, 'mg', totals.caffeine_mg ? totals.caffeine_mg_per_kg + ' mg per kg' : '')
      );
    }
    main.appendChild(tiles);

    /* Pickle shots sits beside the Gel split card rather than alone on its
       own row below a grid it no longer fully fills. */
    var splitRow = el('div', 'eltane-planner__split-row');

    if (carb.flavor_breakdown) {
      var flavors = Object.keys(carb.flavor_breakdown).filter(function (key) {
        return carb.flavor_breakdown[key] > 0;
      });
      if (flavors.length) {
        var gelCard = el('div', 'eltane-planner__gel-card');
        gelCard.appendChild(el('p', 'eltane-planner__flavors-label', 'Recommended gel split'));
        var gelRows = el('ul', 'eltane-planner__gel-rows');
        flavors.forEach(function (key) {
          var row = el('li', 'eltane-planner__gel-row');
          row.appendChild(el('span', 'eltane-planner__gel-name', FLAVOR_LABEL[key] || key));
          row.appendChild(el('span', 'eltane-planner__gel-qty', String(carb.flavor_breakdown[key])));
          gelRows.appendChild(row);
        });
        gelCard.appendChild(gelRows);
        splitRow.appendChild(gelCard);
      }
    }

    if (cramp.qty_pickle_shots !== undefined) {
      splitRow.appendChild(tile('Pickle shots', num(cramp.qty_pickle_shots)));
    }

    if (splitRow.children.length) main.appendChild(splitRow);

    /* ---- when to take everything — the core of the planner's answer. */
    var timeline = this.renderTimeline(plan.events || []);
    if (timeline) main.appendChild(timeline);

    var legsCard = this.renderLegs(plan.legs || []);
    if (legsCard) main.appendChild(legsCard);

    var fluidCard = this.renderFluids(fluids);
    if (fluidCard) main.appendChild(fluidCard);

    var carryCard = this.renderCarryList(plan.carry_list, sodiumCheck.instructions);
    if (carryCard) main.appendChild(carryCard);

    if (cramp.note) {
      var crampCard = el('div', 'eltane-planner__info-card');
      crampCard.appendChild(el('p', 'eltane-planner__flavors-label', 'Cramp plan'));
      crampCard.appendChild(el('p', 'eltane-planner__info-line', crampCopy(cramp.note)));
      if (pickle.note) crampCard.appendChild(el('p', 'eltane-planner__info-line eltane-planner__info-line--muted', crampCopy(pickle.note)));
      main.appendChild(crampCard);
    }

    /* ---- anything the planner wants the athlete to know. Warnings first:
       a plan that cannot deliver its carb target, or a hyponatraemia risk,
       matters more than a reminder to practise in training. LOW_SODIUM's
       message is the same text as sodium_plan.electrolyte_note, so the flag
       alone carries it. */
    /* "error" flags (e.g. PICKLE_ON_RUN) are the planner's own guard-rails,
       logged for the tech team, not advice for the athlete. */
    var flags = (plan.flags || []).filter(function (flag) {
      return flag && flag.level !== 'error' && !(flag.message && isHiddenNote(flag.message));
    }).sort(function (a, b) {
      return (a.level === 'warning' ? 0 : 1) - (b.level === 'warning' ? 0 : 1);
    });
    var notes = [carb.coffee_gel_note].concat(plan.notes || []).filter(function (note) {
      return note && note !== pickle.note && !isHiddenNote(note);
    });
    if (flags.length || notes.length) {
      var heads = el('div', 'eltane-planner__heads-up');
      heads.appendChild(el('p', 'eltane-planner__flavors-label', 'Good to know'));
      flags.forEach(function (flag) {
        if (!flag || !flag.message) return;
        heads.appendChild(
          el(
            'p',
            'eltane-planner__flag eltane-planner__flag--' + (flag.level === 'warning' ? 'warning' : 'info'),
            flag.message
          )
        );
      });
      notes.forEach(function (note) {
        heads.appendChild(el('p', 'eltane-planner__note-line', crampCopy(note)));
      });
      main.appendChild(heads);
    }

    main.appendChild(el('p', 'eltane-planner__disclaimer', PLAN_DISCLAIMER));

    /* Filled in by deliverPlan() once the address has actually been used for
       something — empty and hidden until then, so the panel never shows a
       promise about an email before there is one to report. */
    var contactStatus = el('p', 'eltane-planner__contact-status');
    contactStatus.setAttribute('data-planner-contact-status', '');
    contactStatus.setAttribute('role', 'status');
    contactStatus.hidden = true;
    main.appendChild(contactStatus);

    this.renderCart(aside, plan.cart || []);
  };

  /*
   * The race-day timeline: every gel (and the cramp-prone pre-race pickle
   * shot) at its clock time from the start gun, with where on the course
   * that falls. Built entirely from the API's `events` — the times are the
   * planner's, never re-derived here.
   */
  Planner.prototype.renderTimeline = function (events) {
    if (!events.length) return null;

    var wrap = el('div', 'eltane-planner__race-timeline');
    wrap.appendChild(el('p', 'eltane-planner__result-lead', 'Your race-day timeline'));
    wrap.appendChild(
      el('p', 'eltane-planner__race-timeline-help', 'Clock time from the start gun. Negative times are before the start.')
    );

    var list = el('ol', 'eltane-planner__race-events');
    events.forEach(function (event, index) {
      var kind = eventKind(event);
      var row = reveal(el('li', 'eltane-planner__race-event eltane-planner__race-event--' + kind), index);
      row.appendChild(el('span', 'eltane-planner__race-clock', event.clock));

      var body = el('div', 'eltane-planner__race-body');
      body.appendChild(el('p', 'eltane-planner__race-title', eventTitle(event)));
      if (event.position) body.appendChild(el('p', 'eltane-planner__race-position', event.position));
      if (event.note) body.appendChild(el('p', 'eltane-planner__race-note', event.note));
      row.appendChild(body);

      list.appendChild(row);
    });
    wrap.appendChild(list);
    return wrap;
  };

  Planner.prototype.renderFluids = function (fluids) {
    if (fluids.ml_per_h === undefined) return null;

    var card = el('div', 'eltane-planner__info-card');
    card.appendChild(el('p', 'eltane-planner__flavors-label', 'Fluids'));

    var lines = [];
    var range = fluids.range_ml_per_h;
    lines.push(
      'About ' + fluids.ml_per_h + ' ml per hour' + (range ? ' (' + range[0] + '–' + range[1] + ' ml)' : '') +
        (fluids.total_ml !== undefined ? ', ' + fluids.total_ml + ' ml in total.' : '.')
    );
    if (fluids.sip_ml) lines.push('Sip about ' + fluids.sip_ml + ' ml every ' + fluids.sip_every_min + ' min.');
    if (fluids.by_leg_ml) {
      lines.push('Bike ' + fluids.by_leg_ml.bike + ' ml · Run ' + fluids.by_leg_ml.run + ' ml.');
    }
    if (fluids.pre_race && fluids.pre_race.instruction) lines.push('Before the race: ' + fluids.pre_race.instruction);

    lines.forEach(function (line) {
      card.appendChild(el('p', 'eltane-planner__info-line', line));
    });
    (fluids.notes || []).forEach(function (note) {
      card.appendChild(el('p', 'eltane-planner__info-line eltane-planner__info-line--muted', note));
    });
    return card;
  };

  function plural(n, word) {
    return n + ' ' + word + (n === 1 ? '' : 's');
  }

  function durationLabel(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    return h ? h + ' h' + (m ? ' ' + m + ' min' : '') : m + ' min';
  }

  /*
   * The carb budget per leg (spec v2.1 Section 6): gels carry their share of
   * the target, sports drink and food fill the gap, electrolytes cover sodium.
   * One column per leg — a triathlon shows bike and run side by side.
   */
  Planner.prototype.renderLegs = function (legs) {
    if (!legs.length) return null;
    var triathlon = this.sportValue() === 'Triathlon';
    var LEG_LABEL = { bike: 'Bike', ride: 'Ride', hyrox: 'Race', run: triathlon ? 'Run' : 'Race' };

    var card = el('div', 'eltane-planner__info-card eltane-planner__legs');
    card.appendChild(el('p', 'eltane-planner__flavors-label', 'Carb budget'));

    var grid = el('div', 'eltane-planner__legs-grid');
    grid.style.setProperty('--legs', String(legs.length));
    legs.forEach(function (leg, index) {
      var col = reveal(el('div', 'eltane-planner__leg'), index);
      var head = el('p', 'eltane-planner__leg-head');
      head.appendChild(el('span', 'eltane-planner__leg-name', LEG_LABEL[leg.leg] || leg.leg));
      head.appendChild(
        el('span', 'eltane-planner__leg-meta', durationLabel(leg.minutes) + ' · ' + leg.rate_g_per_h + ' g / hr')
      );
      col.appendChild(head);

      var rows = [['Target', leg.target_g + ' g'], ['Gels', plural(leg.gels, 'gel') + ' · ' + leg.gels_g + ' g']];
      if (leg.water_only) {
        rows.push(['Fill', leg.gap_g ? 'Water only (' + leg.gap_g + ' g gap is small)' : 'Water only']);
      } else {
        rows.push(['Sports drink', leg.drink_g + ' g in ' + leg.sports_drink_ml + ' ml']);
        if (leg.food_portions) rows.push(['Food', plural(leg.food_portions, 'portion') + ' · ' + leg.food_g + ' g']);
      }
      rows.push(['Water', leg.water_ml + ' ml']);
      if (leg.electrolyte_servings) rows.push(['Electrolyte', plural(leg.electrolyte_servings, 'serving')]);

      var list = el('dl', 'eltane-planner__leg-rows');
      rows.forEach(function (row) {
        var line = el('div', 'eltane-planner__leg-row');
        line.appendChild(el('dt', null, row[0]));
        line.appendChild(el('dd', null, row[1]));
        list.appendChild(line);
      });
      col.appendChild(list);
      grid.appendChild(col);
    });
    card.appendChild(grid);
    return card;
  };

  /* What the athlete must pack (spec v2.1 Section 6, step 10). Sports drink,
     food and electrolytes are not Eltane products, so they live here and
     never in the cart. */
  Planner.prototype.renderCarryList = function (carry, electrolyteLines) {
    if (!carry) return null;

    var card = el('div', 'eltane-planner__info-card');
    card.appendChild(el('p', 'eltane-planner__flavors-label', 'What to carry'));

    var items = [plural(carry.gels || 0, 'gel')];
    if (carry.pickle_shots) items.push(plural(carry.pickle_shots, 'pickle juice shot'));
    if (carry.sports_drink_ml) items.push('About ' + carry.sports_drink_ml + ' ml sports drink');
    if (carry.food_portions) items.push(plural(carry.food_portions, 'food portion') + ' (about 25 g carbs each)');
    if (carry.electrolyte_servings) items.push(plural(carry.electrolyte_servings, 'electrolyte serving'));

    var list = el('ul', 'eltane-planner__carry');
    items.forEach(function (item) { list.appendChild(el('li', null, item)); });
    card.appendChild(list);

    var byLeg = carry.by_leg;
    if (byLeg && byLeg.bike) {
      var bike = byLeg.bike;
      var onBike = [plural(bike.gels, 'gel')];
      if (bike.sports_drink_ml) onBike.push(bike.sports_drink_ml + ' ml sports drink');
      if (bike.food_portions) onBike.push(plural(bike.food_portions, 'food portion'));
      if (bike.electrolyte_servings) onBike.push(plural(bike.electrolyte_servings, 'electrolyte serving'));
      card.appendChild(el('p', 'eltane-planner__info-line', 'On the bike: ' + onBike.join(', ') + '.'));
    }
    if (byLeg && byLeg.run_belt) {
      card.appendChild(
        el('p', 'eltane-planner__info-line', 'In the run belt: ' + plural(byLeg.run_belt.gels, 'gel') + '.')
      );
      if (byLeg.run_belt.note) {
        card.appendChild(el('p', 'eltane-planner__info-line eltane-planner__info-line--muted', byLeg.run_belt.note));
      }
    }

    (electrolyteLines || []).forEach(function (line) {
      card.appendChild(el('p', 'eltane-planner__info-line', line));
    });
    if (carry.line) card.appendChild(el('p', 'eltane-planner__info-line eltane-planner__info-line--muted', carry.line));
    return card;
  };

  Planner.prototype.renderDownload = function () {
    var self = this;
    var actions = el('div', 'eltane-planner__plan-actions');

    var button = el('button', 'button button--secondary', 'Download my plan');
    button.type = 'button';
    button.setAttribute('data-planner-download', '');
    button.addEventListener('click', function () { self.downloadPlan(button); });
    actions.appendChild(button);

    var status = el('p', 'eltane-planner__download-status');
    status.setAttribute('data-planner-download-status', '');
    status.setAttribute('role', 'status');
    status.hidden = true;
    actions.appendChild(status);

    return actions;
  };

  /*
   * The pack panel. Sticky beside the plan on desktop (see the stylesheet), so
   * the products stay reachable however far down the timeline the visitor
   * reads — the previous flat list sat below everything and was only found by
   * scrolling past the whole plan.
   */
  Planner.prototype.renderCart = function (host, items) {
    var self = this;
    var pack = el('div', 'eltane-planner__pack');
    host.appendChild(pack);

    pack.appendChild(el('h3', 'eltane-planner__pack-title', 'Your Race Day Pack'));
    pack.appendChild(
      el('p', 'eltane-planner__pack-note', 'Everything the plan above calls for, in one add.')
    );

    var table = el('ul', 'eltane-planner__cart');
    var unmapped = [];

    items.forEach(function (item, itemIndex) {
      var mapped = self.productMap[item.product_id];
      var includedByDefault = item.quantity > 0;
      var isOptional = !!item.optional_add_on && !includedByDefault && mapped;

      var row = reveal(el('li', 'eltane-planner__cart-row'), itemIndex);
      if (!includedByDefault && !isOptional) row.className += ' eltane-planner__cart-row--empty';

      var media = el('div', 'eltane-planner__cart-media');
      if (mapped && mapped.image) {
        var img = document.createElement('img');
        img.src = mapped.image;
        /* Named rather than decorative: this thumbnail is the only image the
           product has here, and the row's title is a link, so a blank alt left
           screen readers with a link and no picture of what it points at. */
        img.alt = mapped.title || '';
        img.loading = 'lazy';
        media.appendChild(img);
      } else {
        media.className += ' eltane-planner__cart-media--placeholder';
      }
      row.appendChild(media);

      var main = el('div', 'eltane-planner__cart-main');
      var titleText = (mapped && mapped.title) || item.title;
      var titleNode;
      if (mapped && mapped.url) {
        titleNode = document.createElement('a');
        titleNode.className = 'eltane-planner__cart-title';
        titleNode.href = mapped.url;
        titleNode.textContent = titleText;
      } else {
        titleNode = el('span', 'eltane-planner__cart-title', titleText);
      }
      main.appendChild(titleNode);

      /* Price and pack size are left off this list on purpose — the planner
         shows what to carry, and the cart shows what it costs. */
      /* The "Optional add-on" badge only earns its place when there's no
         checkbox on this row to say the same thing — an item that's already
         in the cart (qty > 0) but still skippable has nothing else marking
         it as such, while a row WITH the "Add to plan" toggle below (see
         isOptional) would just be repeating itself, wrapping onto its own
         line under the price for no reason. */
      var showBadge = item.optional_add_on && !isOptional;
      if (showBadge) {
        var meta = el('span', 'eltane-planner__cart-meta');
        meta.appendChild(el('span', 'eltane-planner__badge', 'Optional add-on'));
        main.appendChild(meta);
      }

      row.appendChild(main);

      /* Optional recommendations (e.g. a cramp-relief shot the rules didn't
         trigger) start unchecked; the shopper opts in instead of getting it
         forced into the cart. */
      /* Per-item quantity used to show here (e.g. "3") — now shown once, in
         the Gel split card above, instead of repeated on every row here. */
      if (isOptional) {
        var toggle = el('label', 'eltane-planner__cart-toggle');
        var checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.setAttribute('data-planner-optional', item.product_id);
        toggle.appendChild(checkbox);
        toggle.appendChild(el('span', null, 'Add to plan'));
        row.appendChild(toggle);
      }

      table.appendChild(row);

      if (includedByDefault && !mapped) unmapped.push(item.product_id);
    });

    pack.appendChild(table);

    var actions = el('div', 'eltane-planner__result-actions');

    /* Read live, not once: an optional item toggled on after this panel
       first rendered must still be included in the single add-to-cart call. */
    function collectAddable() {
      return items
        .filter(function (item) {
          var mapped = self.productMap[item.product_id];
          if (!mapped) return false;
          if (item.quantity > 0) return true;
          if (!item.optional_add_on) return false;
          var checkbox = table.querySelector('[data-planner-optional="' + item.product_id + '"]');
          return !!(checkbox && checkbox.checked);
        })
        .map(function (item) {
          var copy = {};
          for (var key in item) {
            if (Object.prototype.hasOwnProperty.call(item, key)) copy[key] = item[key];
          }
          if (!(copy.quantity > 0)) copy.quantity = 1;
          return copy;
        });
    }

    var canAddAnything = items.some(function (item) {
      return self.productMap[item.product_id] && (item.quantity > 0 || item.optional_add_on);
    });

    if (canAddAnything) {
      var add = el('button', 'button button--primary', 'ADD TO CART');
      add.type = 'button';
      add.addEventListener('click', function () {
        var selected = collectAddable();
        var status = self.root.querySelector('[data-planner-cart-status]');
        if (!selected.length) {
          if (status) {
            status.textContent = 'Select at least one item to add to your cart.';
            status.hidden = false;
          }
          return;
        }
        self.addToCart(add, selected);
      });
      actions.appendChild(add);
    }

    var restart = el('button', 'button button--secondary', 'Start over');
    restart.type = 'button';
    restart.setAttribute('data-planner-restart', '');
    restart.addEventListener('click', function () {
      self.resetFields();
      self.editing = false;
      self.panel('wizard');
      self.show(0);
    });
    actions.appendChild(restart);
    pack.appendChild(actions);

    if (unmapped.length) {
      pack.appendChild(
        el(
          'p',
          'eltane-planner__mapping-warning',
          'Not yet linked to Shopify products: ' +
            unmapped.join(', ') +
            '. In the theme editor, open this section and set these under "Product mapping" to enable add-to-cart.'
        )
      );
    }

    var status = el('div', 'eltane-planner__cart-status');
    status.setAttribute('data-planner-cart-status', '');
    status.hidden = true;
    status.setAttribute('role', 'status');
    pack.appendChild(status);
  };

  /* ============================================================ plan image
     The finished plan drawn to a canvas and handed over as a PNG.

     Drawn rather than screenshotted: the results panel is a two-column layout
     built for a browser, and there is no library in this theme that could
     rasterise it. Composing the card directly also means the export can be
     any shape the content actually calls for, sized to what's drawn below
     rather than cropped out of a web page.
     ============================================================ */

  /* 1200 x 906 logical units at 2x = a 2400 x 1812 PNG. */
  /* maxEvents: since spec v2.1 a plan can hold 16 gels plus pickle shots and
     food portions (an Ironman is 25 rows), so the cap sits well above that. */
  var PLAN_IMAGE = { width: 1200, height: 906, scale: 2, pad: 80, eventRow: 46, maxEvents: 40 };

  function cssVar(name, fallback) {
    var value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  }

  function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, w, h, r);
      return;
    }
    /* Safari before 16 and Firefox before 112 have no roundRect. */
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* Greedy wrap, capped at `maxLines` with an ellipsis on the last one — a
     note that overflows its card is worse than one that is cut short. */
  function wrapLines(ctx, text, maxWidth, maxLines) {
    var words = String(text).split(/\s+/);
    var lines = [];
    var line = '';

    for (var i = 0; i < words.length; i++) {
      var candidate = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(candidate).width <= maxWidth || !line) {
        line = candidate;
        continue;
      }
      lines.push(line);
      line = words[i];
      if (lines.length === maxLines) break;
    }

    if (lines.length < maxLines && line) lines.push(line);

    if (lines.length === maxLines) {
      var last = lines[maxLines - 1];
      var consumed = lines.join(' ').length;
      if (consumed < String(text).length) {
        while (last.length > 1 && ctx.measureText(last + '…').width > maxWidth) {
          last = last.slice(0, -1);
        }
        lines[maxLines - 1] = last + '…';
      }
    }

    return lines;
  }

  Planner.prototype.drawPlanImage = function () {
    var plan = this.plan || {};
    var carb = plan.carb_plan || {};
    var sodium = plan.sodium_plan || {};
    var cramp = plan.cramp_plan || {};
    var calc = plan.calculated || {};
    var totalsFromPlanner = plan.summary || {};
    var fluids = plan.fluids || {};

    var pad = PLAN_IMAGE.pad;
    var contentWidth = PLAN_IMAGE.width - pad * 2;

    /* The timeline is the part of the plan worth having on race morning, so
       the card grows to hold it rather than squeezing it out. Capped so a
       pathological event count cannot produce a poster-length image. */
    var events = (plan.events || []).slice(0, PLAN_IMAGE.maxEvents);
    var timelineTop = 796;
    var timelineHeight = events.length ? 60 + events.length * PLAN_IMAGE.eventRow : 0;
    var imageHeight = PLAN_IMAGE.height + timelineHeight;

    var canvas = document.createElement('canvas');
    canvas.width = PLAN_IMAGE.width * PLAN_IMAGE.scale;
    canvas.height = imageHeight * PLAN_IMAGE.scale;
    var ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(PLAN_IMAGE.scale, PLAN_IMAGE.scale);

    /* A light card regardless of the site theme: this leaves the page to be
       saved to a camera roll and printed, and a dark export is a poor citizen
       in both. */
    var ink = '#0f1419';
    var muted = '#6b7280';
    var hairline = '#e4e7eb';
    var wash = '#f5f7f9';
    /* --eltane-primary, not --eltane-accent: the accent is tuned for light
       text on the site's dark panels and is too pale to read on white. */
    var accent = cssVar('--eltane-primary', '#367ab3');

    var heading = cssVar('--font-eltane-heading', 'ui-sans-serif, system-ui, sans-serif');
    var body = cssVar('--font-eltane-body', heading);
    var mono = cssVar('--font-eltane-mono', 'ui-monospace, monospace');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, PLAN_IMAGE.width, imageHeight);
    ctx.textBaseline = 'alphabetic';

    function rule(y) {
      ctx.fillStyle = hairline;
      ctx.fillRect(pad, y, contentWidth, 1);
    }

    function eyebrow(text, y) {
      ctx.fillStyle = accent;
      ctx.font = '500 20px ' + mono;
      ctx.textAlign = 'left';
      ctx.fillText(String(text).toUpperCase(), pad, y);
    }

    /* ---- header */
    ctx.fillStyle = ink;
    ctx.font = '600 26px ' + mono;
    ctx.textAlign = 'left';
    ctx.fillText((this.brand || 'Fuelling plan').toUpperCase(), pad, 96);

    /* Only when there is a brand on the left to sit opposite — without one the
       two ends of the header say the same thing twice. */
    if (this.brand) {
      ctx.fillStyle = muted;
      ctx.font = '400 22px ' + mono;
      ctx.textAlign = 'right';
      ctx.fillText('FUELLING PLAN', PLAN_IMAGE.width - pad, 96);
    }
    rule(130);

    /* ---- title */
    var name = this.plannerName();
    var sportHeading = SPORT_RESULT_HEADING[this.sportValue()] || 'Your Fuel Planner';

    ctx.textAlign = 'left';
    ctx.fillStyle = ink;
    ctx.font = '700 62px ' + heading;
    ctx.fillText(name ? 'Hey ' + name + ',' : sportHeading, pad, 220);

    ctx.fillStyle = muted;
    ctx.font = '400 24px ' + body;
    ctx.fillText(name ? sportHeading : 'Your personalised fuelling plan', pad, 258);

    /* ---- the answers it was built from */
    var pairs = this.summaryPairs(calc).slice(0, 6);
    if (pairs.length) {
      var gap = 14;
      var boxWidth = (contentWidth - gap * (pairs.length - 1)) / pairs.length;
      pairs.forEach(function (pair, index) {
        var x = pad + index * (boxWidth + gap);
        ctx.fillStyle = wash;
        roundedRect(ctx, x, 292, boxWidth, 96, 16);
        ctx.fill();

        ctx.fillStyle = muted;
        ctx.font = '500 16px ' + mono;
        ctx.fillText(String(pair[0]).toUpperCase(), x + 18, 326);

        ctx.fillStyle = ink;
        ctx.font = '600 24px ' + body;
        ctx.fillText(wrapLines(ctx, pair[1], boxWidth - 36, 1)[0] || '', x + 18, 362);
      });
    }

    /* ---- the two numbers the whole plan turns on */
    eyebrow('Your race, per hour', 442);

    var carbsPerHour =
      totalsFromPlanner.carbs_g_per_h !== undefined ? totalsFromPlanner.carbs_g_per_h : carb.target_carbs_hr_g;
    var perHour = [
      { label: 'Carbohydrate', value: num(carbsPerHour), unit: 'g / hr' },
      fluids.ml_per_h !== undefined
        ? { label: 'Fluids', value: fluids.ml_per_h, unit: 'ml / hr' }
        : { label: 'Sodium', value: num(sodium.target_sodium_hr_mg), unit: 'mg / hr' }
    ];
    perHour.forEach(function (stat, index) {
      var boxWidth = (contentWidth - 20) / 2;
      var x = pad + index * (boxWidth + 20);
      ctx.fillStyle = wash;
      roundedRect(ctx, x, 466, boxWidth, 168, 20);
      ctx.fill();

      ctx.fillStyle = muted;
      ctx.font = '500 18px ' + mono;
      ctx.fillText(String(stat.label).toUpperCase(), x + 28, 508);

      ctx.fillStyle = ink;
      ctx.font = '700 66px ' + heading;
      ctx.fillText(String(stat.value), x + 28, 580);

      var valueWidth = ctx.measureText(String(stat.value)).width;
      ctx.fillStyle = accent;
      ctx.font = '500 22px ' + mono;
      ctx.fillText(stat.unit, x + 38 + valueWidth, 580);
    });

    /* ---- the totals.
       Duration is not repeated here — it already has a chip above, and a card
       that says the same number twice in 300px wastes a slot the caffeine and
       cramp figures need. */
    var totals = [];
    if (calc.speed_kmh) totals.push(['Avg speed', calc.speed_kmh + ' km/h']);
    else if (calc.pace) totals.push(['Avg pace', String(calc.pace)]);
    totals.push(
      ['Total carbs', num(totalsFromPlanner.carbs_g !== undefined ? totalsFromPlanner.carbs_g : carb.total_carbs_g) + ' g'],
      ['Performance gels', String(num(totalsFromPlanner.gels !== undefined ? totalsFromPlanner.gels : carb.qty_gels))],
      ['Pickle shots', String(num(cramp.qty_pickle_shots))]
    );
    if (totalsFromPlanner.caffeine_mg !== undefined) {
      totals.push(['Caffeine', totalsFromPlanner.caffeine_mg + ' mg']);
    }

    var totalsGap = 14;
    var totalsWidth = (contentWidth - totalsGap * (totals.length - 1)) / totals.length;
    totals.forEach(function (total, index) {
      var x = pad + index * (totalsWidth + totalsGap);
      ctx.strokeStyle = hairline;
      ctx.lineWidth = 1;
      roundedRect(ctx, x, 656, totalsWidth, 108, 16);
      ctx.stroke();

      ctx.fillStyle = muted;
      ctx.font = '500 16px ' + mono;
      ctx.fillText(String(total[0]).toUpperCase(), x + 18, 692);

      ctx.fillStyle = ink;
      ctx.font = '600 28px ' + heading;
      /* A pace reads "5:58 /km" — long enough to leave its box at five cards
         across, so it is clipped rather than allowed to run into its neighbour. */
      ctx.fillText(wrapLines(ctx, total[1], totalsWidth - 36, 1)[0] || '', x + 18, 732);
    });

    /* ---- race-day timeline: clock, what to take, where on the course */
    if (events.length) {
      eyebrow('Race-day timeline · clock from the start gun', timelineTop + 24);
      events.forEach(function (event, index) {
        var y = timelineTop + 60 + index * PLAN_IMAGE.eventRow;
        rule(y);
        var baseline = y + 31;

        ctx.textAlign = 'left';
        ctx.fillStyle = accent;
        ctx.font = '600 22px ' + mono;
        ctx.fillText(String(event.clock || ''), pad, baseline);

        ctx.fillStyle = ink;
        ctx.font = (event.flavour === 'coffee' ? '700 ' : '500 ') + '22px ' + body;
        ctx.fillText(wrapLines(ctx, eventTitle(event), 470, 1)[0] || '', pad + 120, baseline);

        ctx.textAlign = 'right';
        ctx.fillStyle = muted;
        ctx.font = '400 20px ' + body;
        ctx.fillText(wrapLines(ctx, event.position || '', contentWidth - 620, 1)[0] || '', PLAN_IMAGE.width - pad, baseline);
      });
    }

    /* ---- footer */
    var footerTop = 822 + timelineHeight;
    rule(footerTop);
    ctx.fillStyle = muted;
    ctx.font = '400 20px ' + mono;
    ctx.textAlign = 'left';
    ctx.fillText(this.site || '', pad, footerTop + 40);
    ctx.textAlign = 'right';
    ctx.fillText('Built with the ' + (this.brand || 'Eltane') + ' fuel planner', PLAN_IMAGE.width - pad, footerTop + 40);

    return canvas;
  };

  Planner.prototype.planFilename = function () {
    var stamp = new Date().toISOString().slice(0, 10);
    var parts = ['fuel-plan', this.plannerName(), this.sportValue(), stamp];
    return (
      parts
        .filter(Boolean)
        .join('-')
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') + '.png'
    );
  };

  Planner.prototype.downloadPlan = function (button) {
    var self = this;
    var status = this.root.querySelector('[data-planner-download-status]');

    function report(message) {
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }

    function finish() {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = 'Download my plan';
    }

    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Preparing…';
    report('');

    /* Wait for the webfonts before drawing. Canvas takes whatever is loaded at
       the moment fillText runs and does not redraw when a face arrives later,
       so measuring against a fallback and painting with the real font is how
       an export ends up with text overrunning its box. */
    var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();

    ready
      .catch(function () { /* A font that fails to load is not a reason to refuse the download. */ })
      .then(function () {
        var canvas = self.drawPlanImage();
        if (!canvas || !canvas.toBlob) throw new Error('This browser cannot render the plan image.');

        canvas.toBlob(function (blob) {
          if (!blob) {
            report('Could not build the image. Try again, or screenshot the plan above.');
            finish();
            return;
          }

          var url = window.URL.createObjectURL(blob);
          var link = document.createElement('a');
          link.href = url;
          link.download = self.planFilename();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          /* Released on the next turn of the loop rather than immediately —
             revoking in the same tick cancels the download in Safari. */
          window.setTimeout(function () { window.URL.revokeObjectURL(url); }, 1000);

          /* The browser's own download UI already confirms this — a second,
             on-page "Saved as fuel-plan-....png" repeats what the browser
             just showed rather than adding anything. The failure states
             below still report, since those aren't otherwise visible. */
          report('');
          finish();
        }, 'image/png');
      })
      .catch(function (error) {
        report(error.message || 'Could not build the image.');
        finish();
      });
  };

  /* Patches the header's cart icon/count from a /cart/add.js response's
     `sections['cart-icon-bubble']` HTML — the same section + selector
     cart.js itself reads (see getSectionsToRender there). Only used as a
     fallback when the theme has neither <cart-notification> nor
     <cart-drawer> (see cartUIComponent) — normally that component's own
     getSectionsToRender() already includes cart-icon-bubble, and
     renderContents() repaints it as part of the same call. */
  function updateCartIconBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    var parsed = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
    var content = parsed.querySelector('.shopify-section');
    if (content) bubble.innerHTML = content.innerHTML;
  }

  /* The theme's own cart UI — <cart-notification> (a toast) or <cart-drawer>
     (a slide-out panel), whichever this header uses. Every other
     add-to-cart button on the site (see product-form.js) keeps this in sync
     by requesting its sections and calling .renderContents() on success.
     Our own addToCart used a raw fetch() that skipped this entirely: the
     header bubble count still got patched (see updateCartIconBubble above),
     but the drawer/notification's own cached markup — rendered once at
     page load, when the cart may still have been empty — never refreshed.
     That's why opening the drawer right after "Add plan to cart" kept
     showing "Your cart is empty" even though the real Shopify cart already
     had the items; a full reload of /cart was fine because that fetches
     fresh from Shopify instead of reading the stale drawer DOM. */
  function cartUIComponent() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /* Repaints cartUI's own sections from a /cart/add.js response WITHOUT
     calling its renderContents() — that also pops the drawer/notification
     open, which here would slide right over the page's own "N items added"
     + Notify-me prompts we just built for whatever failed. This keeps the
     drawer's cached DOM correct for whenever the shopper does open it
     themselves, while leaving today's on-page confirmation as the only
     thing that appears unasked. Mirrors renderContents()'s own patching
     logic (see CartDrawer/CartNotification) minus the final open() call. */
  function refreshCartUISections(cartUI, body) {
    if (!cartUI || !body || !body.sections || typeof cartUI.getSectionsToRender !== 'function') return;
    cartUI.getSectionsToRender().forEach(function (section) {
      var html = body.sections[section.id];
      if (!html) return;
      var target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!target) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var content = parsed.querySelector(section.selector || '.shopify-section');
      if (content) target.innerHTML = content.innerHTML;
    });
    if (cartUI.classList.contains('is-empty')) cartUI.classList.remove('is-empty');
  }

  /* A "Notify me" block identical in behaviour to snippets/eltane-notify-me.liquid,
     built in JS because the sold-out item here is one of the planner's own
     recommendation cards — never rendered through Liquid's product loop, so
     there's no product object to render that snippet from. Reuses the same
     markup contract ([data-notify-me] + friends) and CSS/JS asset (added
     alongside this section, see eltane-fuel-planner.liquid) rather than
     duplicating the email-capture logic. */
  Planner.prototype.buildNotifyBlock = function (line) {
    var container = el('div', 'eltane-notify eltane-planner__notify');
    container.setAttribute('data-notify-me', '');
    container.setAttribute('data-api-base', this.apiBase);
    if (this.apiSecretKey) container.setAttribute('data-api-secret-key', this.apiSecretKey);
    container.setAttribute('data-product-id', line.productId || '');
    container.setAttribute('data-variant-id', line.variantId || '');
    container.setAttribute('data-product-title', line.title || '');
    container.setAttribute('data-product-url', line.url || '');

    var openButton = el('button', 'button button--secondary', 'Notify me when available');
    openButton.type = 'button';
    openButton.setAttribute('data-notify-open', '');
    container.appendChild(openButton);

    var form = el('div', 'eltane-notify__form');
    form.setAttribute('data-notify-form', '');
    form.hidden = true;

    var emailInput = document.createElement('input');
    emailInput.type = 'email';
    emailInput.className = 'eltane-notify__input';
    emailInput.placeholder = 'you@email.com';
    emailInput.autocomplete = 'email';
    emailInput.required = true;
    emailInput.setAttribute('data-notify-email', '');
    form.appendChild(emailInput);

    var submitButton = el('button', 'button button--primary', 'Notify me');
    submitButton.type = 'button';
    submitButton.setAttribute('data-notify-submit', '');
    form.appendChild(submitButton);
    container.appendChild(form);

    var notifyStatus = el('p', 'eltane-notify__status');
    notifyStatus.setAttribute('data-notify-status', '');
    notifyStatus.setAttribute('role', 'status');
    notifyStatus.hidden = true;
    container.appendChild(notifyStatus);

    return container;
  };

  /* "The maximum quantity of this item is already in your cart" (and its
     sibling "...does not have enough stock" once the cart already holds
     some) mean the item is NOT unavailable — it's already sitting in the
     cart, just capped there by stock. Notify-me makes no sense for that: it
     promises to tell the shopper when something they already have becomes
     available. Only genuine unavailability (sold out, no longer available)
     earns the prompt. */
  function isQuantityCapError(message) {
    return !!message && /maximum quantity/i.test(message);
  }

  /* Renders what addToCart() found once every line has been attempted:
     a confirmation for whatever made it in, plus a Notify-me prompt for
     each line that didn't (rather than losing the sale on the items that
     WERE in stock — see addToCart's own comment for why this can no
     longer just redirect straight to /cart on any failure). */
  Planner.prototype.renderCartAddResult = function (status, added, failed) {
    if (!status) return;
    status.innerHTML = '';
    status.hidden = false;

    if (added.length) {
      status.appendChild(
        el(
          'p',
          'eltane-planner__cart-status-line',
          added.length === 1 ? '1 item added to your cart.' : added.length + ' items added to your cart.'
        )
      );
    }

    var self = this;
    failed.forEach(function (entry) {
      /* Nothing to tell the shopper here — the item IS already in their
         cart (just not added by this particular click, e.g. a repeat click
         across a page reload, which resets the in-memory
         cartAddedVariantIds guard — see addToCart). Showing an error for
         something that already succeeded would only alarm them, and
         Notify-me makes no sense for an item they already have. */
      if (isQuantityCapError(entry.message)) return;

      var row = el('div', 'eltane-planner__cart-status-failed');
      row.appendChild(
        el('p', 'eltane-planner__cart-status-line', entry.message || (entry.line.title || 'This item') + ' could not be added to your cart.')
      );
      row.appendChild(self.buildNotifyBlock(entry.line));
      status.appendChild(row);
      /* eltane-notify-me.js binds every [data-notify-me] block it sees on
         this event (see its own listener) — dispatched on `row`, not the
         block itself, since the handler does a descendant search and the
         block IS the [data-notify-me] element. */
      row.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    });
  };

  /* One request per line rather than a single batched call: Shopify's
     /cart/add.js treats an { items: [...] } array atomically — one
     unavailable variant (sold out, insufficient stock) fails the WHOLE
     request and adds nothing, including the other items that were
     perfectly fine. Adding sequentially means a sold-out item blocks only
     itself; see renderCartAddResult for how the split outcome is shown. */
  Planner.prototype.addToCart = function (button, items) {
    var self = this;
    var status = this.root.querySelector('[data-planner-cart-status]');
    /* Persists across clicks on this Planner (not reset by "Start over" —
       a variant already sitting in the real Shopify cart stays there
       either way, so there's nothing to gain by forgetting it). Without
       this, clicking "Add plan to cart" a second time re-POSTs every line
       again, including ones already added — which Shopify rejects with
       "The maximum quantity of this item is already in your cart" and
       which used to get misread as a sold-out item needing Notify me. */
    if (!self.cartAddedVariantIds) self.cartAddedVariantIds = {};

    var lines = [];
    items.forEach(function (item) {
      var mapped = self.productMap[item.product_id];
      self.resolvePackLines(item).forEach(function (packLine) {
        lines.push({
          id: packLine.variantId,
          quantity: packLine.quantity,
          /* The real Shopify product id (from the mapping), NOT item.product_id
             ("P1a"…"P3") — see productMap's own comment on why Notify me needs
             the real one. Singles point at the pack product too: the single
             is hidden, so Notify me and links should land on the pack. */
          productId: mapped && mapped.productId,
          variantId: packLine.variantId,
          title: ((mapped && mapped.title) || item.title) + (packLine.single ? ' (single)' : ''),
          url: mapped && mapped.url
        });
      });
    });
    lines = lines
      .filter(function (line) {
        return !self.cartAddedVariantIds[line.id];
      });

    if (!lines.length) {
      if (status) {
        status.innerHTML = '';
        status.appendChild(
          el('p', 'eltane-planner__cart-status-line', 'Everything in this plan is already in your cart.')
        );
        status.hidden = false;
      }
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    if (status) {
      status.hidden = true;
      status.innerHTML = '';
    }

    var added = [];
    var failed = [];
    var lastBody = null;
    var cartUI = cartUIComponent();
    /* The single-item shorthand ({id, quantity}, no items[] wrapper) rather
       than the batched form used before: it's what product-form.js itself
       sends, and unlike the items[] form it makes /cart/add.js respond with
       the added line item at the TOP level (parsedState.key / .id) instead
       of nested under an `items` array — the shape cartUI.renderContents()
       requires (see CartNotification/CartDrawer's own renderContents). */
    var sectionIds = cartUI ? cartUI.getSectionsToRender().map(function (s) { return s.id; }) : ['cart-icon-bubble'];

    function addLine(index) {
      if (index >= lines.length) return Promise.resolve();
      var line = lines[index];
      return window
        .fetch(self.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ id: line.id, quantity: line.quantity, sections: sectionIds })
        })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Could not add to cart.');
            return body;
          });
        })
        .then(function (body) {
          added.push(line);
          self.cartAddedVariantIds[line.id] = true;
          lastBody = body;
        })
        .catch(function (error) {
          failed.push({ line: line, message: error.message });
        })
        .then(function () {
          return addLine(index + 1);
        });
    }

    addLine(0).then(function () {
      button.disabled = false;
      button.classList.remove('is-loading');

      if (!failed.length) {
        /* Go straight to the cart instead of leaving a confirmation message
           the shopper has to click through — they already asked to buy. */
        window.location.href = self.cartUrl;
        return;
      }

      if (added.length && lastBody) {
        if (cartUI) {
          refreshCartUISections(cartUI, lastBody);
        } else {
          updateCartIconBubble(lastBody.sections);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'fuel-planner', cartData: lastBody });
        }
      }

      self.renderCartAddResult(status, added, failed);
    });
  };

  function init(root) {
    (root || document).querySelectorAll('[data-planner]').forEach(function (node) {
      if (node.dataset.plannerBound === 'true') return;
      node.dataset.plannerBound = 'true';
      new Planner(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
